import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, ChevronRight, Play, Maximize, ArrowRight, Sparkles, Zap, CheckCircle, Check, Home, Inbox, FolderOpen, Timer, Plus, FileText, Users, Calendar, ListTodo, DollarSign, BarChart3, Activity, Network, Search, FileSearch, Scale, Mail, Send, Clock, TrendingUp, AlertTriangle, Brain, Layers, Briefcase, Building2, Link } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// ChatGPT-style Prompt Bar with Typewriter Effect
const PromptReveal = ({ 
  text, 
  isVisible, 
  moveToBottom = false,
  onTypingComplete 
}: { 
  text: string; 
  isVisible: boolean; 
  moveToBottom?: boolean;
  onTypingComplete?: () => void;
}) => {
  const [displayText, setDisplayText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const [typingDone, setTypingDone] = useState(false);
  
  useEffect(() => {
    if (!isVisible) {
      setDisplayText('');
      setTypingDone(false);
      return;
    }
    
    let currentIndex = 0;
    const typeSpeed = 40;
    
    // Start typing after a brief delay
    const startDelay = setTimeout(() => {
      const typeInterval = setInterval(() => {
        if (currentIndex < text.length) {
          setDisplayText(text.slice(0, currentIndex + 1));
          currentIndex++;
        } else {
          clearInterval(typeInterval);
          setTypingDone(true);
          if (onTypingComplete) {
            setTimeout(onTypingComplete, 800);
          }
        }
      }, typeSpeed);
      
      return () => clearInterval(typeInterval);
    }, 400);
    
    // Cursor blink
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 530);
    
    return () => {
      clearTimeout(startDelay);
      clearInterval(cursorInterval);
    };
  }, [isVisible, text, onTypingComplete]);

  return (
    <motion.div
      className="w-full max-w-xl"
      initial={{ opacity: 0, y: 20, scale: 0.96 }}
      animate={{ 
        opacity: isVisible ? 1 : 0, 
        y: isVisible ? 0 : 20,
        scale: isVisible ? 1 : 0.96
      }}
      transition={{ 
        duration: 0.7, 
        ease: [0.22, 1, 0.36, 1]
      }}
    >
      {/* Chat input container */}
      <div 
        className="relative bg-[#18181b] rounded-xl border border-white/[0.06] overflow-hidden"
        style={{
          boxShadow: '0 16px 32px -8px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255,255,255,0.02)'
        }}
      >
        <div className="flex items-center px-4 py-3.5">
          {/* Text area */}
          <div className="flex-1 min-h-[24px] flex items-center">
            <span className="text-white/85 text-base font-normal tracking-[-0.01em]">
              {displayText}
              <motion.span 
                className="inline-block w-[2px] h-[17px] bg-white/50 ml-[1px] align-middle rounded-full"
                animate={{ opacity: showCursor ? 1 : 0 }}
                transition={{ duration: 0.08 }}
              />
            </span>
          </div>
          
          {/* Send button */}
          <motion.div 
            className="ml-3 w-7 h-7 rounded-lg flex items-center justify-center"
            animate={{ 
              backgroundColor: typingDone ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.03)',
              scale: typingDone ? 1 : 0.92
            }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <ArrowRight className={`w-3.5 h-3.5 transition-all duration-300 ${typingDone ? 'text-white/70' : 'text-white/20'}`} />
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

// Animated Words Component - same as landing page
const AnimatedWords = ({ words, className = '' }: { words: string[]; className?: string }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  
  useEffect(() => {
    const currentWord = words[currentIndex];
    
    if (isTyping && !isDeleting) {
      if (displayText.length < currentWord.length) {
        const timeout = setTimeout(() => {
          setDisplayText(currentWord.slice(0, displayText.length + 1));
        }, 80);
        return () => clearTimeout(timeout);
      } else {
        const timeout = setTimeout(() => {
          setIsDeleting(true);
        }, 2000);
        return () => clearTimeout(timeout);
      }
    } else if (isDeleting) {
      if (displayText.length > 0) {
        const timeout = setTimeout(() => {
          setDisplayText(displayText.slice(0, -1));
        }, 40);
        return () => clearTimeout(timeout);
      } else {
        setIsDeleting(false);
        setCurrentIndex((prev) => (prev + 1) % words.length);
      }
    }
  }, [displayText, isTyping, isDeleting, currentIndex, words]);

  return (
    <span className={className}>
      <span style={{ display: 'inline-block', minWidth: '320px', textAlign: 'left' }}>
        {displayText}
        <motion.span
          animate={{ opacity: [1, 0] }}
          transition={{ duration: 0.53, repeat: Infinity, repeatType: 'reverse' }}
          className="inline-block w-[4px] h-[0.85em] bg-gradient-to-b from-purple-400 to-violet-500 ml-0.5 rounded-sm"
          style={{ verticalAlign: 'baseline', marginBottom: '-0.1em' }}
        />
      </span>
    </span>
  );
};

// Slide data
const slides = [
  { id: 1, type: 'cover' },
  { id: 2, type: 'founder-video' },
  { id: 3, type: 'market-need' },
  { id: 4, type: 'how-we-solve' },
  { id: 5, type: 'introducing-lexy' },
  { id: 6, type: 'lexy-walkthrough' },
  { id: 7, type: 'why-now' },
  { id: 8, type: 'market-validation' },
  { id: 9, type: 'market-size' },
  { id: 10, type: 'competitive-landscape' },
  { id: 11, type: 'competitive-advantage' },
  { id: 12, type: 'company' },
  { id: 13, type: 'ai-native' },
  { id: 14, type: 'business-model' },
];

const InvestorPresentation = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'ArrowRight' || e.key === ' ') {
      e.preventDefault();
      setDirection(1);
      setCurrentSlide(prev => Math.min(prev + 1, slides.length - 1));
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setDirection(-1);
      setCurrentSlide(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Escape') {
      // Exit fullscreen if in fullscreen mode, otherwise do nothing
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    } else if (e.key === 'f' || e.key === 'F') {
      toggleFullscreen();
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const slideVariants = {
    enter: () => ({
      opacity: 0,
    }),
    center: {
      opacity: 1,
    },
    exit: () => ({
      opacity: 0,
    }),
  };

  const paginate = (newDirection: number) => {
    const newSlide = currentSlide + newDirection;
    if (newSlide >= 0 && newSlide < slides.length) {
      setDirection(newDirection);
      setCurrentSlide(newSlide);
    }
  };

  // ============================================================================
  // SLIDE 1: COVER - World Class with Exact App Sidebar
  // ============================================================================
  const CoverSlide = ({ onStartPresentation }: { onStartPresentation?: () => void }) => {
    const [phase, setPhase] = useState(0);

    useEffect(() => {
      const timers = [
        setTimeout(() => setPhase(1), 400),      // Show logo after brief pause
        setTimeout(() => setPhase(2), 4500),     // Morph into screenshot
      ];
      return () => timers.forEach(clearTimeout);
    }, []);

    return (
      <div className="relative w-full h-full overflow-hidden bg-[#08080a]">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ x: [0, 30, 0], y: [0, -20, 0], opacity: [0.25, 0.35, 0.25] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-10 right-10 w-[300px] md:w-[500px] h-[300px] md:h-[500px] rounded-full bg-purple-500/25 blur-[80px] md:blur-[120px]"
          />
          <motion.div
            animate={{ x: [0, -20, 0], y: [0, 30, 0], opacity: [0.2, 0.3, 0.2] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            className="absolute bottom-10 left-10 w-[250px] md:w-[400px] h-[250px] md:h-[400px] rounded-full bg-indigo-500/20 blur-[60px] md:blur-[100px]"
          />
        </div>

        {/* Centered Logo - Phase 1 - Elegant minimalist */}
        <motion.div
          className="absolute inset-0 flex flex-col items-center justify-center z-20 px-6"
          initial={{ opacity: 0 }}
          animate={{ 
            opacity: phase >= 1 && phase < 2 ? 1 : 0,
          }}
          transition={{ duration: phase < 2 ? 1.5 : 1 }}
        >
          {/* Subtle ambient glow */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: phase >= 1 && phase < 2 ? 1 : 0
            }}
            transition={{ duration: 2.5, ease: "easeOut" }}
            className="absolute w-[300px] md:w-[500px] h-[300px] md:h-[500px] rounded-full bg-violet-500/[0.08] blur-[100px] md:blur-[150px]"
          />
          
          {/* Horizontal logo layout like navbar */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ 
              opacity: phase >= 1 && phase < 2 ? 1 : 0, 
              y: phase >= 1 && phase < 2 ? 0 : 30,
            }}
            transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-center gap-5 md:gap-8 mb-6 md:mb-8"
          >
            <div className="w-16 h-16 md:w-24 md:h-24 rounded-[18px] md:rounded-[26px] bg-gradient-to-br from-violet-500 via-purple-500 to-violet-600 flex items-center justify-center">
              <Zap className="w-8 h-8 md:w-12 md:h-12 text-white" strokeWidth={1.5} />
            </div>
            <h1 className="text-[64px] md:text-[120px] font-[200] text-white tracking-[-0.03em] leading-none">
              Lexy
            </h1>
          </motion.div>
          
          {/* Tagline - Elegant */}
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ 
              opacity: phase >= 1 && phase < 2 ? 1 : 0,
              y: phase >= 1 && phase < 2 ? 0 : 15
            }}
            transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="text-white/40 text-lg md:text-2xl font-light tracking-[0.02em]"
          >
            The AI platform for modern law firms
          </motion.p>
        </motion.div>

        {/* Tilted Screenshot - Phase 2 - Hidden on mobile, show simple version */}
        <motion.div 
          className="absolute inset-0 z-10 pointer-events-none hidden md:block"
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ 
            opacity: phase >= 2 ? 1 : 0,
            scale: phase >= 2 ? 1 : 0.85,
          }}
          transition={{ 
            duration: 1.8, 
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          <div className="relative mt-6 flex justify-center pl-[18%]" style={{ perspective: '1200px' }}>
            <motion.div 
              className="relative w-[100vw] h-[100vh]"
              initial={{ y: 100, rotateX: 0 }}
              animate={{ 
                y: phase >= 2 ? 0 : 100,
              }}
              transition={{ 
                duration: 1.8, 
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <div
                className="w-full h-full relative"
                style={{ 
                  transform: 'rotateX(42deg) rotateY(26deg) rotateZ(12deg) skew(40deg, -40deg)',
                  transformStyle: 'preserve-3d',
                }}
              >
                <div 
                  className="rounded-2xl overflow-hidden bg-[#0a0a0a] shadow-2xl shadow-purple-500/10 relative"
                  style={{
                    border: '1px solid rgba(255,255,255,0.06)',
                    maskImage: 'linear-gradient(to right, black 0%, black 80%, transparent 100%), linear-gradient(to bottom, black 0%, black 75%, transparent 95%)',
                    WebkitMaskImage: 'linear-gradient(to right, black 0%, black 80%, transparent 100%), linear-gradient(to bottom, black 0%, black 75%, transparent 95%)',
                    maskComposite: 'intersect',
                    WebkitMaskComposite: 'source-in',
                  }}
                >
                  <div className="h-8 border-b border-white/[0.04] flex items-center px-3">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                      <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
                      <div className="w-3 h-3 rounded-full bg-[#28c840]" />
                    </div>
                  </div>
                  <div className="flex min-h-[750px]">
                    <div className="w-56 bg-[#0a0a0a] border-r border-white/[0.06] flex flex-col">
                      <nav className="flex-1 py-4 overflow-hidden">
                        <div className="px-4 pt-2 pb-2.5">
                          <span className="text-[10px] font-medium text-white/15 uppercase tracking-[0.12em]">WORK</span>
                        </div>
                        <div className="px-2 space-y-0.5">
                          {[
                            { icon: Home, label: 'Overview', active: true },
                            { icon: Inbox, label: 'Inbox', badge: '3' },
                            { icon: ListTodo, label: 'Work Queue' },
                            { icon: Sparkles, label: 'AI Workspace' },
                          ].map((item, i) => (
                            <motion.div 
                              key={i}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: phase >= 2 ? 1 : 0, x: phase >= 2 ? 0 : -10 }}
                              transition={{ delay: 0.3 + i * 0.03 }}
                              className={`flex items-center gap-3 px-2.5 h-9 rounded-lg text-[13px] ${item.active ? 'bg-white/[0.08] text-white' : 'text-white/45'}`}
                            >
                              <item.icon className={`w-[17px] h-[17px] ${item.active ? 'text-white/90' : 'text-white/40'}`} strokeWidth={1.5} />
                              <span>{item.label}</span>
                              {item.badge && <span className="ml-auto text-[10px] bg-violet-500/80 text-white px-2 py-0.5 rounded-full">{item.badge}</span>}
                            </motion.div>
                          ))}
                        </div>
                        <div className="px-4 pt-6 pb-2.5">
                          <span className="text-[10px] font-medium text-white/15 uppercase tracking-[0.12em]">CASES</span>
                        </div>
                        <div className="px-2 space-y-0.5">
                          {[
                            { icon: FileText, label: 'Cases' },
                            { icon: Users, label: 'Clients' },
                            { icon: FolderOpen, label: 'Documents' },
                            { icon: Calendar, label: 'Calendar' },
                          ].map((item, i) => (
                            <motion.div 
                              key={i}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: phase >= 2 ? 1 : 0, x: phase >= 2 ? 0 : -10 }}
                              transition={{ delay: 0.4 + i * 0.03 }}
                              className="flex items-center gap-3 px-2.5 h-9 rounded-lg text-[13px] text-white/45"
                            >
                              <item.icon className="w-[17px] h-[17px] text-white/40" strokeWidth={1.5} />
                              <span>{item.label}</span>
                            </motion.div>
                          ))}
                        </div>
                        <div className="px-4 pt-6 pb-2.5">
                          <span className="text-[10px] font-medium text-white/15 uppercase tracking-[0.12em]">ADMIN</span>
                        </div>
                        <div className="px-2 space-y-0.5">
                          {[
                            { icon: Timer, label: 'Time Tracking' },
                            { icon: DollarSign, label: 'Invoicing' },
                            { icon: BarChart3, label: 'Analytics' },
                            { icon: Activity, label: 'Activity' },
                          ].map((item, i) => (
                            <motion.div 
                              key={i}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: phase >= 2 ? 1 : 0, x: phase >= 2 ? 0 : -10 }}
                              transition={{ delay: 0.5 + i * 0.03 }}
                              className="flex items-center gap-3 px-2.5 h-9 rounded-lg text-[13px] text-white/45"
                            >
                              <item.icon className="w-[17px] h-[17px] text-white/40" strokeWidth={1.5} />
                              <span>{item.label}</span>
                            </motion.div>
                          ))}
                        </div>
                        <div className="px-4 pt-6 pb-2.5">
                          <span className="text-[10px] font-medium text-white/15 uppercase tracking-[0.12em]">MORE</span>
                        </div>
                        <div className="px-2 space-y-0.5">
                          <motion.div 
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: phase >= 2 ? 1 : 0, x: phase >= 2 ? 0 : -10 }}
                            transition={{ delay: 0.6 }}
                            className="flex items-center gap-3 px-2.5 h-9 rounded-lg text-[13px] text-white/45"
                          >
                            <Network className="w-[17px] h-[17px] text-white/40" strokeWidth={1.5} />
                            <span>Community</span>
                            <span className="ml-auto text-[9px] bg-emerald-500/80 text-white px-1.5 py-0.5 rounded-full">New</span>
                          </motion.div>
                        </div>
                      </nav>
                    </div>
                    
                    <div className="flex-1 flex flex-col bg-[#0c0c0e] relative overflow-hidden">
                      <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-violet-500/[0.08] blur-[150px]" />
                      </div>
                      <div className="flex-1 flex flex-col items-center justify-center px-8 relative z-10">
                        {/* Horizontal logo layout */}
                        <motion.div 
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 20 }}
                          transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                          className="flex items-center gap-5 mb-4"
                        >
                          <div className="w-14 h-14 rounded-[16px] bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center">
                            <Zap className="w-7 h-7 text-white" strokeWidth={1.5} />
                          </div>
                          <span className="text-[56px] font-[200] text-white tracking-[-0.02em]">Lexy</span>
                        </motion.div>
                        <motion.p
                          initial={{ opacity: 0 }}
                          animate={{ opacity: phase >= 2 ? 1 : 0 }}
                          transition={{ delay: 0.4 }}
                          className="text-white/40 text-xl font-light mb-10"
                        >
                          The AI platform for modern law firms
                        </motion.p>
                        
                        {/* Start Presentation Button - Elegant minimalist */}
                        <motion.div 
                          initial={{ opacity: 0 }}
                          animate={{ opacity: phase >= 2 ? 1 : 0 }}
                          transition={{ duration: 1.2, delay: 0.8 }}
                          className="flex items-center gap-4 px-6 py-3 rounded-full border border-white/[0.1] bg-white/[0.02] backdrop-blur-sm hover:bg-white/[0.04] hover:border-white/[0.15] transition-all duration-500 cursor-pointer"
                        >
                          <div className="w-8 h-8 rounded-full bg-white/[0.08] flex items-center justify-center">
                            <Play className="w-3 h-3 text-white/70 ml-0.5" fill="currentColor" />
                          </div>
                          <span className="text-[14px] text-white/60 font-light tracking-wide">Start Presentation</span>
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Mobile Phase 2 - Simple centered version */}
        <motion.div 
          className="absolute inset-0 z-10 flex md:hidden flex-col items-center justify-center px-8"
          initial={{ opacity: 0 }}
          animate={{ 
            opacity: phase >= 2 ? 1 : 0,
          }}
          transition={{ duration: 1.2 }}
        >
          {/* Horizontal logo layout for mobile */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 20 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-center gap-4 mb-4"
          >
            <div className="w-12 h-12 rounded-[14px] bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-[44px] font-[200] text-white tracking-[-0.02em]">Lexy</span>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 10 }}
            transition={{ duration: 0.8, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="text-white/40 text-[16px] font-light mb-10 text-center"
          >
            The AI platform for modern law firms
          </motion.p>
          
          {/* Start button for mobile - Elegant minimalist */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: phase >= 2 ? 1 : 0 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="flex items-center gap-3 px-5 py-2.5 rounded-full border border-white/[0.1] bg-white/[0.02]"
          >
            <div className="w-7 h-7 rounded-full bg-white/[0.08] flex items-center justify-center">
              <Play className="w-2.5 h-2.5 text-white/70 ml-0.5" fill="currentColor" />
            </div>
            <span className="text-[13px] text-white/50 font-light tracking-wide">Tap to start</span>
          </motion.div>
        </motion.div>

        {/* Full slide clickable overlay - click anywhere to start presentation */}
        {phase >= 2 && (
          <div
            onClick={onStartPresentation}
            data-testid="start-presentation-btn"
            className="absolute inset-0 z-50 cursor-pointer"
          />
        )}
      </div>
    );
  };

  // ============================================================================
  // SLIDE 3: FEATURES - Exciting with App Preview
  // ============================================================================
  const FeaturesBentoSlide = () => {
    const [activeFeature, setActiveFeature] = useState(0);

    const features = [
      { id: 'ai' },
      { id: 'cases' },
      { id: 'time' },
      { id: 'billing' },
      { id: 'clients' },
    ];

    // Auto-rotate
    useEffect(() => {
      const timeout = setTimeout(() => {
        setActiveFeature(prev => (prev + 1) % features.length);
      }, 6000);
      return () => clearTimeout(timeout);
    }, [activeFeature, features.length]);

    // ========== SCENE 1: AI WORKSPACE ==========
    const AIScene = () => (
      <div className="absolute inset-0 flex items-center justify-center">
        {/* Dramatic glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[600px] bg-violet-500/20 blur-[200px] rounded-full" />
        
        <div className="relative z-10 flex items-center gap-24">
          {/* Left - Visual */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            {/* Document stack */}
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="relative"
            >
              {/* Back documents */}
              <div className="absolute -top-3 -left-3 w-72 h-80 bg-white/[0.02] border border-white/[0.04] rounded-2xl" />
              <div className="absolute -top-1.5 -left-1.5 w-72 h-80 bg-white/[0.03] border border-white/[0.06] rounded-2xl" />
              
              {/* Main document */}
              <div className="relative w-72 h-80 bg-[#111113] border border-white/[0.08] rounded-2xl p-6 overflow-hidden">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-white/[0.05] rounded-xl flex items-center justify-center">
                    <FileText className="w-5 h-5 text-white/40" />
                  </div>
                  <div>
                    <p className="text-sm text-white/80 font-medium">Employment Contract</p>
                    <p className="text-xs text-white/30">32 pages • PDF</p>
                  </div>
                </div>
                
                {/* Text lines with highlights */}
                <div className="space-y-3">
                  <div className="h-2.5 bg-white/[0.06] rounded-full w-full" />
                  <div className="h-2.5 bg-white/[0.06] rounded-full w-[85%]" />
                  <div className="h-2.5 bg-violet-500/30 rounded-full w-[70%]" />
                  <div className="h-2.5 bg-white/[0.06] rounded-full w-[90%]" />
                  <div className="h-2.5 bg-amber-500/30 rounded-full w-[60%]" />
                  <div className="h-2.5 bg-white/[0.06] rounded-full w-full" />
                  <div className="h-2.5 bg-white/[0.06] rounded-full w-[75%]" />
                  <div className="h-2.5 bg-emerald-500/30 rounded-full w-[55%]" />
                </div>

                {/* Scanning line effect */}
                <motion.div
                  animate={{ top: ['20%', '80%', '20%'] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute left-0 right-0 h-12 bg-gradient-to-b from-transparent via-violet-500/10 to-transparent"
                />
              </div>
            </motion.div>

            {/* Floating analysis cards */}
            <motion.div
              initial={{ opacity: 0, x: 30, y: -20 }}
              animate={{ opacity: 1, x: 40, y: -30 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="absolute -right-8 top-8 bg-[#111113] border border-violet-500/30 rounded-xl px-4 py-3 shadow-xl shadow-violet-500/10"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-violet-500/20 rounded-lg flex items-center justify-center">
                  <Brain className="w-4 h-4 text-violet-400" />
                </div>
                <div>
                  <p className="text-[11px] text-violet-400 font-medium">KEY CLAUSE</p>
                  <p className="text-xs text-white/70">24-month non-compete</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20, y: 20 }}
              animate={{ opacity: 1, x: 30, y: 30 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="absolute -right-4 top-32 bg-[#111113] border border-amber-500/30 rounded-xl px-4 py-3 shadow-xl shadow-amber-500/10"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                </div>
                <div>
                  <p className="text-[11px] text-amber-400 font-medium">RISK</p>
                  <p className="text-xs text-white/70">Missing liability cap</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 10, y: 40 }}
              animate={{ opacity: 1, x: 20, y: 50 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="absolute -right-2 top-56 bg-[#111113] border border-emerald-500/30 rounded-xl px-4 py-3 shadow-xl shadow-emerald-500/10"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                </div>
                <div>
                  <p className="text-[11px] text-emerald-400 font-medium">SUGGESTION</p>
                  <p className="text-xs text-white/70">Add arbitration clause</p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right - Copy */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-lg"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-violet-500/10 border border-violet-500/20 rounded-full mb-8"
            >
              <Sparkles className="w-4 h-4 text-violet-400" />
              <span className="text-sm text-violet-300 font-medium">AI Workspace</span>
            </motion.div>
            
            <h2 className="text-[64px] font-semibold text-white leading-[1.05] tracking-[-0.03em] mb-6">
              Read contracts<br />in seconds.
            </h2>
            
            <p className="text-xl text-white/40 leading-relaxed">
              Upload any document. Get instant analysis with key clauses, risks, and actionable recommendations.
            </p>
          </motion.div>
        </div>
      </div>
    );

    // ========== SCENE 2: CASES ==========
    const CasesScene = () => (
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-500/15 blur-[200px] rounded-full" />
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full mb-8"
          >
            <Briefcase className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-blue-300 font-medium">Case Management</span>
          </motion.div>
          
          <h2 className="text-[64px] font-semibold text-white leading-[1.05] tracking-[-0.03em] mb-6">
            Every case. One place.
          </h2>
          
          <p className="text-xl text-white/40 max-w-xl mx-auto">
            Track phases, deadlines, and documents. Nothing falls through the cracks.
          </p>
        </motion.div>

        {/* Case timeline */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="relative z-10 flex items-center gap-6"
        >
          {[
            { phase: 'Discovery', status: 'done', days: '12 days' },
            { phase: 'Analysis', status: 'done', days: '8 days' },
            { phase: 'Negotiation', status: 'active', days: 'In progress' },
            { phase: 'Settlement', status: 'pending', days: 'Upcoming' },
          ].map((item, i) => (
            <React.Fragment key={item.phase}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 + i * 0.15 }}
                className="flex flex-col items-center"
              >
                <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mb-4 ${
                  item.status === 'done' ? 'bg-emerald-500' :
                  item.status === 'active' ? 'bg-blue-500 shadow-lg shadow-blue-500/30' :
                  'bg-white/[0.06] border border-white/[0.08]'
                }`}>
                  {item.status === 'done' ? (
                    <CheckCircle className="w-8 h-8 text-white" />
                  ) : item.status === 'active' ? (
                    <motion.div
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="w-4 h-4 bg-white rounded-full"
                    />
                  ) : (
                    <div className="w-4 h-4 bg-white/20 rounded-full" />
                  )}
                </div>
                <p className={`text-lg font-medium mb-1 ${
                  item.status === 'active' ? 'text-white' : 'text-white/60'
                }`}>{item.phase}</p>
                <p className={`text-sm ${
                  item.status === 'active' ? 'text-blue-400' : 'text-white/30'
                }`}>{item.days}</p>
              </motion.div>
              
              {i < 3 && (
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 0.7 + i * 0.15, duration: 0.4 }}
                  className={`w-24 h-1 rounded-full origin-left ${
                    item.status === 'done' ? 'bg-emerald-500' : 'bg-white/[0.08]'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </motion.div>
      </div>
    );

    // ========== SCENE 3: TIME TRACKING ==========
    const TimeScene = () => (
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-emerald-500/15 blur-[200px] rounded-full" />
        
        <div className="relative z-10 flex items-center gap-24">
          {/* Left - Copy */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-lg"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-8"
            >
              <Timer className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-emerald-300 font-medium">Time Tracking</span>
            </motion.div>
            
            <h2 className="text-[64px] font-semibold text-white leading-[1.05] tracking-[-0.03em] mb-6">
              Never miss a<br />billable hour.
            </h2>
            
            <p className="text-xl text-white/40 leading-relaxed">
              AI detects your work automatically. One click to approve. Effortless accuracy.
            </p>
          </motion.div>

          {/* Right - Visual */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            {/* Big time display */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="text-center mb-8"
            >
              <p className="text-[100px] font-light text-white tracking-tight leading-none">
                6<span className="text-white/30">:</span>45
              </p>
              <p className="text-lg text-white/30 mt-2">hours today</p>
            </motion.div>

            {/* Time entries */}
            <div className="space-y-3 w-80">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
                className="flex items-center gap-4 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl"
              >
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-emerald-400" />
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">Contract review</p>
                  <p className="text-sm text-emerald-400">Auto-detected</p>
                </div>
                <p className="text-xl font-semibold text-emerald-400">2:30</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.75 }}
                className="flex items-center gap-4 p-4 bg-white/[0.03] border border-white/[0.08] rounded-2xl"
              >
                <div className="w-12 h-12 bg-white/[0.05] rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-white/40" />
                </div>
                <div className="flex-1">
                  <p className="text-white/80 font-medium">Client meeting</p>
                  <p className="text-sm text-white/40">Hansen & Partners</p>
                </div>
                <p className="text-xl font-medium text-white/50">1:15</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9 }}
                className="flex items-center gap-4 p-4 bg-white/[0.03] border border-white/[0.08] rounded-2xl"
              >
                <div className="w-12 h-12 bg-white/[0.05] rounded-xl flex items-center justify-center">
                  <Search className="w-6 h-6 text-white/40" />
                </div>
                <div className="flex-1">
                  <p className="text-white/80 font-medium">Legal research</p>
                  <p className="text-sm text-white/40">Berg employment case</p>
                </div>
                <p className="text-xl font-medium text-white/50">3:00</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    );

    // ========== SCENE 4: INVOICING ==========
    const InvoicingScene = () => (
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-amber-500/15 blur-[200px] rounded-full" />
        
        <div className="relative z-10 flex items-center gap-24">
          {/* Left - Visual */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            {/* Invoice card */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="w-80 bg-[#111113] border border-white/[0.08] rounded-3xl p-8 shadow-2xl"
            >
              <div className="flex items-center justify-between mb-8">
                <div>
                  <p className="text-sm text-white/40 mb-1">Invoice</p>
                  <p className="text-lg text-white font-medium">#2024-0847</p>
                </div>
                <div className="px-4 py-2 bg-emerald-500/20 rounded-full">
                  <span className="text-sm text-emerald-400 font-medium">Paid</span>
                </div>
              </div>

              <div className="mb-8">
                <p className="text-[56px] font-semibold text-white leading-none">125,000</p>
                <p className="text-lg text-white/30 mt-1">NOK</p>
              </div>

              <div className="flex items-center justify-between pt-6 border-t border-white/[0.06]">
                <div>
                  <p className="text-sm text-white/40">Client</p>
                  <p className="text-white font-medium">Nordvik AS</p>
                </div>
                <div className="flex items-center gap-2 text-emerald-400">
                  <TrendingUp className="w-5 h-5" />
                  <span className="font-medium">+24%</span>
                </div>
              </div>
            </motion.div>

            {/* Stats floating */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="absolute -bottom-6 -right-6 bg-[#111113] border border-white/[0.08] rounded-2xl px-6 py-4"
            >
              <p className="text-3xl font-semibold text-white">1.2M</p>
              <p className="text-sm text-white/40">This month</p>
            </motion.div>
          </motion.div>

          {/* Right - Copy */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-lg"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-full mb-8"
            >
              <DollarSign className="w-4 h-4 text-amber-400" />
              <span className="text-sm text-amber-300 font-medium">Invoicing</span>
            </motion.div>
            
            <h2 className="text-[64px] font-semibold text-white leading-[1.05] tracking-[-0.03em] mb-6">
              Get paid<br />faster.
            </h2>
            
            <p className="text-xl text-white/40 leading-relaxed">
              Generate professional invoices instantly. Track payments automatically. Focus on law, not admin.
            </p>
          </motion.div>
        </div>
      </div>
    );

    // ========== SCENE 5: CLIENTS ==========
    const ClientsScene = () => (
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-rose-500/15 blur-[200px] rounded-full" />
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-rose-500/10 border border-rose-500/20 rounded-full mb-8"
          >
            <Users className="w-4 h-4 text-rose-400" />
            <span className="text-sm text-rose-300 font-medium">Client Portal</span>
          </motion.div>
          
          <h2 className="text-[64px] font-semibold text-white leading-[1.05] tracking-[-0.03em] mb-6">
            Transparency builds trust.
          </h2>
          
          <p className="text-xl text-white/40 max-w-xl mx-auto">
            Clients track progress, review documents, and message you directly. All in one beautiful portal.
          </p>
        </motion.div>

        {/* Client cards */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="relative z-10 flex items-center gap-6"
        >
          {[
            { name: 'Nordvik AS', initials: 'NA', color: 'from-rose-400 to-pink-500', cases: 3, status: 'online' },
            { name: 'Hansen & Co', initials: 'HC', color: 'from-blue-400 to-cyan-500', cases: 2, status: 'offline' },
            { name: 'Berg Industries', initials: 'BI', color: 'from-violet-400 to-purple-500', cases: 1, status: 'online' },
          ].map((client, i) => (
            <motion.div
              key={client.name}
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.5 + i * 0.15 }}
              className={`w-64 bg-[#111113] border border-white/[0.08] rounded-3xl p-6 ${
                i === 0 ? 'ring-2 ring-rose-500/30' : ''
              }`}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${client.color} flex items-center justify-center text-white text-lg font-semibold`}>
                  {client.initials}
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">{client.name}</p>
                  <p className="text-sm text-white/40">{client.cases} active cases</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${
                    client.status === 'online' ? 'bg-emerald-500' : 'bg-white/20'
                  }`} />
                  <span className="text-sm text-white/40 capitalize">{client.status}</span>
                </div>
                <ChevronRight className="w-5 h-5 text-white/20" />
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    );

    const scenes = [AIScene, CasesScene, TimeScene, InvoicingScene, ClientsScene];
    const CurrentScene = scenes[activeFeature];

    return (
      <div className="relative w-full h-full overflow-hidden bg-[#08080a]">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeFeature}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="absolute inset-0"
          >
            <CurrentScene />
          </motion.div>
        </AnimatePresence>

        {/* Progress dots - minimal */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2">
          {features.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveFeature(i)}
              className={`transition-all duration-500 rounded-full ${
                activeFeature === i 
                  ? 'w-8 h-2 bg-white/60' 
                  : 'w-2 h-2 bg-white/20 hover:bg-white/30'
              }`}
            />
          ))}
        </div>
      </div>
    );
  };

  // ============================================================================
  // SLIDE 2: FOUNDER VIDEO - World-Class Cinematic Experience
  // ============================================================================
  const FounderVideoSlide = () => {
    const videoRef = React.useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [phase, setPhase] = useState<'init' | 'typing' | 'sent' | 'aiTyping' | 'aiDone' | 'fadeOut' | 'video'>('init');
    const [userText, setUserText] = useState('');
    const [aiText, setAiText] = useState('');
    
    const userMessage = "What's the vision behind Lexy?";
    const aiMessage = "Let us show you.";

    useEffect(() => {
      // Start after a brief pause for dramatic effect
      const initDelay = setTimeout(() => {
        setPhase('typing');
        let currentIndex = 0;
        
        const typeInterval = setInterval(() => {
          if (currentIndex < userMessage.length) {
            setUserText(userMessage.slice(0, currentIndex + 1));
            currentIndex++;
          } else {
            clearInterval(typeInterval);
            
            // Pause after user message
            setTimeout(() => {
              setPhase('sent');
              
              // AI starts typing
              setTimeout(() => {
                setPhase('aiTyping');
                let aiIndex = 0;
                
                const aiTypeInterval = setInterval(() => {
                  if (aiIndex < aiMessage.length) {
                    setAiText(aiMessage.slice(0, aiIndex + 1));
                    aiIndex++;
                  } else {
                    clearInterval(aiTypeInterval);
                    setPhase('aiDone');
                    
                    // Dramatic pause before reveal
                    setTimeout(() => {
                      setPhase('fadeOut');
                      setTimeout(() => setPhase('video'), 700);
                    }, 1200);
                  }
                }, 50);
              }, 700);
            }, 600);
          }
        }, 45);
      }, 800);
      
      return () => clearTimeout(initDelay);
    }, []);

    const handlePlay = () => {
      if (videoRef.current) {
        videoRef.current.play();
        setIsPlaying(true);
      }
    };

    const showChat = phase !== 'video';
    const isFadingOut = phase === 'fadeOut';

    return (
      <div className="relative w-full h-full overflow-hidden flex flex-col items-center justify-center bg-[#08080a]">
        {/* Animated purple orbs - same as other slides */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ 
              x: [0, 20, 0], 
              y: [0, -15, 0], 
              opacity: [0.25, 0.35, 0.25]
            }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -top-32 -right-32 w-[800px] h-[800px] rounded-full bg-violet-600/25 blur-[180px]"
          />
          <motion.div
            animate={{ x: [0, -15, 0], y: [0, 20, 0], opacity: [0.2, 0.3, 0.2] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            className="absolute -bottom-32 -left-32 w-[700px] h-[700px] rounded-full bg-indigo-600/20 blur-[160px]"
          />
        </div>

        {/* Chat conversation - centered */}
        <motion.div 
          className="absolute inset-0 flex items-center justify-center z-20 px-4 md:px-8"
          initial={{ opacity: 0 }}
          animate={{ 
            opacity: isFadingOut ? 0 : (showChat ? 1 : 0),
            scale: isFadingOut ? 0.95 : 1,
            y: isFadingOut ? -30 : 0,
          }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          style={{ pointerEvents: phase === 'video' ? 'none' : 'auto' }}
        >
          <div className="w-full max-w-sm md:max-w-lg space-y-4 md:space-y-5">
            {/* User message */}
            <motion.div 
              className="flex justify-end"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: phase !== 'init' ? 1 : 0, x: phase !== 'init' ? 0 : 20 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <div 
                className="bg-gradient-to-br from-[#2d2d30] to-[#252528] rounded-[16px] md:rounded-[20px] rounded-br-[6px] px-4 md:px-5 py-3 md:py-3.5 max-w-[80%] md:max-w-sm"
                style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}
              >
                <span className="text-white/95 text-[13px] md:text-[15px] leading-relaxed font-normal">
                  {userText}
                  {phase === 'typing' && (
                    <motion.span 
                      className="inline-block w-[2px] h-[13px] md:h-[15px] bg-white/60 ml-0.5 align-middle rounded-full"
                      animate={{ opacity: [1, 0, 1] }}
                      transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
                    />
                  )}
                </span>
              </div>
            </motion.div>

            {/* AI response */}
            <AnimatePresence>
              {(phase === 'aiTyping' || phase === 'aiDone' || phase === 'fadeOut') && (
                <motion.div 
                  className="flex justify-start"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="flex gap-2 md:gap-3 max-w-[80%] md:max-w-sm">
                    {/* AI avatar with glow */}
                    <div className="relative flex-shrink-0">
                      <div className="absolute inset-0 bg-violet-500/40 rounded-full blur-md" />
                      <div className="relative w-7 h-7 md:w-8 md:h-8 rounded-full bg-gradient-to-br from-violet-500 via-purple-500 to-violet-600 flex items-center justify-center shadow-lg">
                        <Sparkles className="w-3.5 h-3.5 md:w-4 md:h-4 text-white" />
                      </div>
                    </div>
                    <div 
                      className="bg-gradient-to-br from-[#1c1c1f] to-[#161618] rounded-[16px] md:rounded-[20px] rounded-tl-[6px] px-4 md:px-5 py-3 md:py-3.5"
                      style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}
                    >
                      <span className="text-white/90 text-[13px] md:text-[15px] leading-relaxed font-normal">
                        {aiText}
                        {phase === 'aiTyping' && (
                          <motion.span 
                            className="inline-block w-[2px] h-[13px] md:h-[15px] bg-violet-400/60 ml-0.5 align-middle rounded-full"
                            animate={{ opacity: [1, 0, 1] }}
                            transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
                          />
                        )}
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Title - shows with video */}
        <AnimatePresence>
          {phase === 'video' && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="absolute top-8 md:top-12 left-0 right-0 text-center z-20 px-4"
            >
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-1 md:mb-2">Our Vision</h2>
              <p className="text-gray-400 text-sm md:text-base">Why we are building Lexy</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Video container - Product Walkthrough style */}
        <AnimatePresence>
          {phase === 'video' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-[92%] md:w-[85%] max-w-5xl aspect-video rounded-xl md:rounded-2xl overflow-hidden shadow-2xl shadow-black/50 z-10"
            >
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                controls={isPlaying}
                playsInline
                preload="auto"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
              >
                <source src="https://customer-assets.emergentagent.com/job_lexy-ui-update/artifacts/v2ioq4ug_Lexy%20Founders%20Intro.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              
              {/* Play button overlay */}
              {!isPlaying && (
                <div 
                  className="absolute inset-0 flex items-center justify-center cursor-pointer bg-black/30 hover:bg-black/20 transition-all duration-500"
                  onClick={handlePlay}
                >
                  <motion.div 
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.3 }}
                    className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-2xl"
                  >
                    <Play className="w-8 h-8 text-gray-900 ml-1" fill="currentColor" />
                  </motion.div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  // ============================================================================
  // SLIDE 3: LEXY WALKTHROUGH VIDEO - Product Demo
  // ============================================================================
  const LexyWalkthroughSlide = () => {
    const videoRef = React.useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);

    const handlePlay = () => {
      if (videoRef.current) {
        videoRef.current.play();
        setIsPlaying(true);
      }
    };

    return (
      <div className="relative w-full h-full overflow-hidden flex flex-col items-center justify-center bg-[#08080a]">
        {/* Animated purple orbs - same as slide 2 */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ 
              x: [0, 20, 0], 
              y: [0, -15, 0], 
              opacity: [0.25, 0.35, 0.25]
            }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -top-32 -right-32 w-[400px] md:w-[800px] h-[400px] md:h-[800px] rounded-full bg-violet-600/25 blur-[100px] md:blur-[180px]"
          />
          <motion.div
            animate={{ x: [0, -15, 0], y: [0, 20, 0], opacity: [0.2, 0.3, 0.2] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            className="absolute -bottom-32 -left-32 w-[350px] md:w-[700px] h-[350px] md:h-[700px] rounded-full bg-indigo-600/20 blur-[80px] md:blur-[160px]"
          />
        </div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="absolute top-8 md:top-12 left-0 right-0 text-center z-20 px-4"
        >
          <h2 className="text-2xl md:text-3xl font-semibold text-white mb-1 md:mb-2">Product Walkthrough</h2>
          <p className="text-gray-400 text-sm md:text-base">See Lexy in action</p>
        </motion.div>

        {/* Video container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-[92%] md:w-[85%] max-w-5xl aspect-video rounded-xl md:rounded-2xl overflow-hidden shadow-2xl shadow-black/50 z-10"
        >
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            controls={isPlaying}
            playsInline
            preload="metadata"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
          >
            <source src="https://customer-assets.emergentagent.com/job_b4ac04fd-4de4-4a31-82e8-651ea7956ecf/artifacts/7dud2nk6_Lexy%20Product%20Walkthrough%20New%202030%20%281%29.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          
          {/* Play button overlay */}
          {!isPlaying && (
            <div 
              className="absolute inset-0 flex items-center justify-center cursor-pointer bg-black/30 hover:bg-black/20 transition-all duration-500"
              onClick={handlePlay}
            >
              <motion.div 
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.3 }}
                className="w-14 h-14 md:w-20 md:h-20 bg-white rounded-full flex items-center justify-center shadow-2xl"
              >
                <Play className="w-8 h-8 text-gray-900 ml-1" fill="currentColor" />
              </motion.div>
            </div>
          )}
        </motion.div>
      </div>
    );
  };

  // ============================================================================
  // SLIDE 5: MARKET NEED - Apple Level
  // ============================================================================
  const MarketNeedSlide = () => {
    const [phase, setPhase] = useState(0);

    useEffect(() => {
      const timers = [
        setTimeout(() => setPhase(1), 600),
        setTimeout(() => setPhase(2), 2200),
        setTimeout(() => setPhase(3), 2800),
        setTimeout(() => setPhase(4), 3600),
        setTimeout(() => setPhase(5), 4400),  // Show bullets
      ];
      return () => timers.forEach(clearTimeout);
    }, []);

    return (
      <div className="relative w-full h-full overflow-hidden bg-[#08080a]">
        {/* Animated purple orbs - exact same as root */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ 
              x: [0, 20, 0], 
              y: [0, -15, 0], 
              opacity: [0.25, 0.35, 0.25]
            }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -top-32 -right-32 w-[800px] h-[800px] rounded-full bg-violet-600/25 blur-[180px]"
          />
          <motion.div
            animate={{ x: [0, -15, 0], y: [0, 20, 0], opacity: [0.2, 0.3, 0.2] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            className="absolute -bottom-32 -left-32 w-[700px] h-[700px] rounded-full bg-indigo-600/20 blur-[160px]"
          />
        </div>

        {/* Phase 1-2: Opening label */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 1 && phase < 3 ? 1 : 0 }}
          transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <span className="text-[15px] font-[400] text-white/40 tracking-[0.4em] uppercase">
            The Problem
          </span>
        </motion.div>

        {/* Phase 3+: Main content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10 px-6">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: phase >= 3 ? 1 : 0 }}
              transition={{ duration: 1.5, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <h2 className="text-2xl sm:text-4xl md:text-[4.5rem] font-[200] text-white tracking-[-0.03em] leading-tight md:leading-[1.15]">
                Legal tech adopted AI.
              </h2>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: phase >= 4 ? 1 : 0 }}
              transition={{ duration: 1.5, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <h2 className="text-2xl sm:text-4xl md:text-[4.5rem] font-[200] text-white/30 tracking-[-0.03em] leading-tight md:leading-[1.15] mt-1 md:mt-2">
                But never rebuilt for it.
              </h2>
            </motion.div>
          </div>

          {/* Bullets - more professional design */}
          <motion.div
            className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6 mt-10 md:mt-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: phase >= 5 ? 1 : 0, y: phase >= 5 ? 0 : 20 }}
            transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {['Fragmented tools', 'Bolted-on AI', 'No context'].map((item, i) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: phase >= 5 ? 1 : 0, y: phase >= 5 ? 0 : 10 }}
                transition={{ duration: 0.6, delay: i * 0.15, ease: [0.25, 0.1, 0.25, 1] }}
                className="px-4 md:px-5 py-2 md:py-2.5 rounded-full bg-white/[0.03] border border-white/[0.08]"
              >
                <span className="text-[12px] md:text-[14px] font-light text-white/50">
                  {item}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // SLIDE 4: THE SOLUTION - Apple Level
  // ============================================================================
  const HowWeSolveSlide = () => {
    const [phase, setPhase] = useState(0);

    useEffect(() => {
      const timers = [
        setTimeout(() => setPhase(1), 600),
        setTimeout(() => setPhase(2), 1800),
        setTimeout(() => setPhase(3), 2400),
        setTimeout(() => setPhase(4), 3200),
      ];
      return () => timers.forEach(clearTimeout);
    }, []);

    const workflowSteps = [
      { label: 'Inbox', icon: Inbox },
      { label: 'Case', icon: Briefcase },
      { label: 'Documents', icon: FileText },
      { label: 'Time', icon: Clock },
      { label: 'Invoice', icon: DollarSign },
    ];

    return (
      <div className="relative w-full h-full overflow-hidden bg-[#08080a]">
        {/* Animated purple orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ 
              x: [0, 20, 0], 
              y: [0, -15, 0], 
              opacity: [0.25, 0.35, 0.25]
            }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -top-32 -right-32 w-[800px] h-[800px] rounded-full bg-violet-600/25 blur-[180px]"
          />
          <motion.div
            animate={{ x: [0, -15, 0], y: [0, 20, 0], opacity: [0.2, 0.3, 0.2] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            className="absolute -bottom-32 -left-32 w-[700px] h-[700px] rounded-full bg-indigo-600/20 blur-[160px]"
          />
        </div>

        {/* Phase 1: Opening label */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 1 && phase < 2 ? 1 : 0 }}
          transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <span className="text-[15px] font-[400] text-white/40 tracking-[0.4em] uppercase">
            The Solution
          </span>
        </motion.div>

        {/* Phase 2+: Main content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10 px-6">
          {/* Title */}
          <motion.div
            className="text-center mb-8 md:mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 20 }}
            transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <h2 className="text-2xl sm:text-4xl md:text-[4rem] font-[200] text-white tracking-[-0.03em] leading-tight md:leading-[1.15]">
              One system.
            </h2>
            <h2 className="text-2xl sm:text-4xl md:text-[4rem] font-[200] text-white/30 tracking-[-0.03em] leading-tight md:leading-[1.15] mt-1 md:mt-2">
              From inbox to invoice.
            </h2>
          </motion.div>

          {/* Workflow boxes */}
          <motion.div
            className="flex flex-wrap justify-center items-center gap-2 md:gap-3 max-w-lg md:max-w-none"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: phase >= 3 ? 1 : 0, y: phase >= 3 ? 0 : 30 }}
            transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {workflowSteps.map((step, i) => (
              <React.Fragment key={step.label}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: phase >= 3 ? 1 : 0, scale: phase >= 3 ? 1 : 0.9 }}
                  transition={{ duration: 0.5, delay: i * 0.1, ease: [0.25, 0.1, 0.25, 1] }}
                  className="flex flex-col items-center gap-2 md:gap-3 px-4 md:px-8 py-3 md:py-6 rounded-xl md:rounded-2xl bg-white/[0.03] border border-white/[0.08]"
                >
                  <step.icon className="w-4 h-4 md:w-6 md:h-6 text-white/50" strokeWidth={1.5} />
                  <span className="text-xs md:text-sm text-white/60 font-light">{step.label}</span>
                </motion.div>
                {i < workflowSteps.length - 1 && (
                  <motion.div
                    initial={{ opacity: 0, scaleX: 0 }}
                    animate={{ opacity: phase >= 3 ? 1 : 0, scaleX: phase >= 3 ? 1 : 0 }}
                    transition={{ duration: 0.3, delay: 0.3 + i * 0.1, ease: [0.25, 0.1, 0.25, 1] }}
                    className="hidden md:block w-8 h-[1px] bg-white/20"
                  />
                )}
              </React.Fragment>
            ))}
          </motion.div>

          {/* Tagline */}
          <motion.p
            className="mt-8 md:mt-12 text-sm md:text-lg text-white/30 font-light"
            initial={{ opacity: 0 }}
            animate={{ opacity: phase >= 4 ? 1 : 0 }}
            transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
          >
            AI-powered at every step
          </motion.p>
        </div>
      </div>
    );
  };

  // ============================================================================
  // SLIDE 5: INTRODUCING LEXY - Simple & Elegant
  // ============================================================================
  const IntroducingLexySlide = () => {
    const [phase, setPhase] = useState(0);

    useEffect(() => {
      const timers = [
        setTimeout(() => setPhase(1), 600),      // Show "Introducing"
        setTimeout(() => setPhase(2), 2400),     // Logo reveal
        setTimeout(() => setPhase(3), 4500),     // Transition to app mockup
      ];
      return () => timers.forEach(clearTimeout);
    }, []);

    // Features for the overview
    const features = [
      { icon: Sparkles, title: 'AI Workspace', desc: 'Intelligent document analysis', color: 'violet' },
      { icon: Briefcase, title: 'Case Management', desc: 'Track every case end-to-end', color: 'blue' },
      { icon: Clock, title: 'Time Tracking', desc: 'AI-powered time capture', color: 'emerald' },
      { icon: DollarSign, title: 'Invoicing', desc: 'Get paid faster', color: 'amber' },
      { icon: Users, title: 'Client Portal', desc: 'Real-time client access', color: 'rose' },
      { icon: FolderOpen, title: 'Documents', desc: 'Centralized & searchable', color: 'cyan' },
    ];

    const getColorClasses = (color: string) => {
      const colors: Record<string, { bg: string; text: string }> = {
        violet: { bg: 'bg-violet-500/15', text: 'text-violet-400' },
        blue: { bg: 'bg-blue-500/15', text: 'text-blue-400' },
        emerald: { bg: 'bg-emerald-500/15', text: 'text-emerald-400' },
        amber: { bg: 'bg-amber-500/15', text: 'text-amber-400' },
        rose: { bg: 'bg-rose-500/15', text: 'text-rose-400' },
        cyan: { bg: 'bg-cyan-500/15', text: 'text-cyan-400' },
      };
      return colors[color] || colors.violet;
    };

    return (
      <div className="relative w-full h-full overflow-hidden bg-[#08080a]">
        {/* Ambient background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ x: [0, 20, 0], y: [0, -15, 0], opacity: [0.2, 0.3, 0.2] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-violet-600/20 blur-[150px]"
          />
          <motion.div
            animate={{ x: [0, -15, 0], y: [0, 20, 0], opacity: [0.15, 0.25, 0.15] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-indigo-600/15 blur-[130px]"
          />
        </div>

        {/* Phase 1: Introducing label */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center z-30"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 1 && phase < 2 ? 1 : 0 }}
          transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <motion.span 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: phase >= 1 ? 1 : 0, y: phase >= 1 ? 0 : 20 }}
            transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
            className="text-[15px] font-[400] text-white/40 tracking-[0.4em] uppercase"
          >
            Introducing
          </motion.span>
        </motion.div>

        {/* Phase 2: Dramatic Logo Reveal */}
        <motion.div
          className="absolute inset-0 flex flex-col items-center justify-center z-20"
          initial={{ opacity: 0 }}
          animate={{ 
            opacity: phase >= 2 && phase < 3 ? 1 : 0,
          }}
          transition={{ duration: 0.8 }}
        >
          {/* Expanding ring effect */}
          <motion.div
            initial={{ scale: 0, opacity: 0.8 }}
            animate={{ scale: phase >= 2 && phase < 3 ? [0, 3] : 0, opacity: phase >= 2 && phase < 3 ? [0.5, 0] : 0 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="absolute w-40 h-40 rounded-full border border-violet-500/50"
          />
          
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5, filter: 'blur(20px)' }}
            animate={{ 
              opacity: phase >= 2 && phase < 3 ? 1 : 0, 
              scale: phase >= 2 && phase < 3 ? 1 : 0.5,
              filter: phase >= 2 && phase < 3 ? 'blur(0px)' : 'blur(20px)'
            }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div
              animate={{ 
                boxShadow: [
                  '0 0 60px 20px rgba(139, 92, 246, 0.2)',
                  '0 0 100px 40px rgba(139, 92, 246, 0.35)',
                  '0 0 60px 20px rgba(139, 92, 246, 0.2)',
                ]
              }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="w-28 h-28 rounded-3xl bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center mb-8"
            >
              <Zap className="w-14 h-14 text-white" strokeWidth={1.5} />
            </motion.div>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
            animate={{ 
              opacity: phase >= 2 && phase < 3 ? 1 : 0, 
              y: phase >= 2 && phase < 3 ? 0 : 30,
              filter: phase >= 2 && phase < 3 ? 'blur(0px)' : 'blur(10px)'
            }}
            transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="text-5xl md:text-8xl font-light text-white tracking-[-0.04em] mb-3 md:mb-4"
          >
            Lexy
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: phase >= 2 && phase < 3 ? 1 : 0, y: phase >= 2 && phase < 3 ? 0 : 20 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-white/40 text-base md:text-2xl font-light tracking-wide text-center px-4"
          >
            The AI platform for modern law firms
          </motion.p>
        </motion.div>

        {/* Phase 3: True Fullscreen App - Hidden on mobile, show simplified version */}
        <motion.div
          className="absolute inset-0 z-10 hidden md:block"
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: phase >= 3 ? 1 : 0, scale: phase >= 3 ? 1 : 1.02 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="w-full h-full bg-[#0a0a0a] flex">
            {/* Sidebar */}
            <motion.div 
              className="w-56 bg-[#0a0a0a] border-r border-white/[0.06] flex flex-col"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: phase >= 3 ? 1 : 0, x: phase >= 3 ? 0 : -20 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="h-14 flex items-center px-4 border-b border-white/[0.06]">
                <div className="w-8 h-8 bg-gradient-to-r from-violet-600 to-purple-600 rounded-lg flex items-center justify-center">
                  <Zap className="w-4 h-4 text-white" />
                </div>
                <span className="ml-3 font-medium text-[15px] text-white/90">Lexy</span>
              </div>

              <nav className="flex-1 py-3 overflow-hidden">
                <div className="px-4 pt-2 pb-2">
                  <span className="text-[10px] font-medium text-white/20 uppercase tracking-[0.12em]">WORK</span>
                </div>
                <div className="px-2 space-y-0.5">
                  {[
                    { icon: Home, label: 'Overview', active: true },
                    { icon: Inbox, label: 'Inbox', badge: '3' },
                    { icon: ListTodo, label: 'Work Queue' },
                    { icon: Sparkles, label: 'AI Workspace' },
                  ].map((item) => (
                    <div key={item.label} className={`flex items-center gap-3 px-3 h-9 rounded-lg text-[13px] ${item.active ? 'bg-white/[0.08] text-white' : 'text-white/45'}`}>
                      <item.icon className={`w-[17px] h-[17px] ${item.active ? 'text-white/90' : 'text-white/40'}`} strokeWidth={1.5} />
                      <span>{item.label}</span>
                      {item.badge && <span className="ml-auto text-[10px] bg-violet-500/80 text-white px-2 py-0.5 rounded-full">{item.badge}</span>}
                    </div>
                  ))}
                </div>

                <div className="px-4 pt-5 pb-2">
                  <span className="text-[10px] font-medium text-white/20 uppercase tracking-[0.12em]">CASES</span>
                </div>
                <div className="px-2 space-y-0.5">
                  {[
                    { icon: FileText, label: 'Cases' },
                    { icon: Users, label: 'Clients' },
                    { icon: FolderOpen, label: 'Documents' },
                    { icon: Calendar, label: 'Calendar' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-3 px-3 h-9 rounded-lg text-[13px] text-white/45">
                      <item.icon className="w-[17px] h-[17px] text-white/40" strokeWidth={1.5} />
                      <span>{item.label}</span>
                    </div>
                  ))}
                </div>

                <div className="px-4 pt-5 pb-2">
                  <span className="text-[10px] font-medium text-white/20 uppercase tracking-[0.12em]">ADMIN</span>
                </div>
                <div className="px-2 space-y-0.5">
                  {[
                    { icon: Clock, label: 'Time Tracking' },
                    { icon: DollarSign, label: 'Invoicing' },
                    { icon: BarChart3, label: 'Analytics' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-3 px-3 h-9 rounded-lg text-[13px] text-white/45">
                      <item.icon className="w-[17px] h-[17px] text-white/40" strokeWidth={1.5} />
                      <span>{item.label}</span>
                    </div>
                  ))}
                </div>
              </nav>

              <div className="px-2 py-2 border-t border-white/[0.06]">
                <div className="flex items-center gap-3 px-3 h-9 rounded-lg text-white/40 text-[13px]">
                  <Building2 className="w-[17px] h-[17px]" strokeWidth={1.5} />
                  <span>Organisation</span>
                </div>
              </div>

              <div className="border-t border-white/[0.06] p-2">
                <div className="flex items-center gap-3 px-3 h-11 rounded-lg">
                  <div className="relative">
                    <div className="w-7 h-7 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-medium text-[11px]">JK</div>
                    <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-[#0a0a0a]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-medium text-white/70 truncate">Jonas Kviteberg</p>
                    <p className="text-[10px] text-white/30">Online</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Main content area */}
            <div className="flex-1 bg-[#0c0c0e] relative overflow-hidden">
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-violet-500/[0.06] blur-[180px]" />
              </div>
                
              <motion.div 
                className="relative z-10 h-full flex flex-col items-center justify-center px-12"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: phase >= 3 ? 1 : 0, y: phase >= 3 ? 0 : 30 }}
                transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: phase >= 3 ? 1 : 0, scale: phase >= 3 ? 1 : 0.8 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  className="w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center mb-6 shadow-2xl shadow-purple-500/25"
                >
                  <Zap className="w-10 h-10 text-white" strokeWidth={1.5} />
                </motion.div>
                  
                <motion.h2
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: phase >= 3 ? 1 : 0, y: phase >= 3 ? 0 : 15 }}
                  transition={{ duration: 0.8, delay: 0.7 }}
                  className="text-5xl font-light text-white tracking-[-0.02em] mb-3"
                >
                  Welcome to Lexy
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: phase >= 3 ? 1 : 0 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                  className="text-white/40 text-xl mb-14"
                >
                  The complete platform for modern law firms
                </motion.p>
                  
                <motion.div 
                  className="grid grid-cols-3 gap-4 w-full max-w-3xl"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: phase >= 3 ? 1 : 0, y: phase >= 3 ? 0 : 20 }}
                  transition={{ duration: 0.8, delay: 0.9 }}
                >
                  {features.map((feature, i) => {
                    const colors = getColorClasses(feature.color);
                    return (
                      <motion.div
                        key={feature.title}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: phase >= 3 ? 1 : 0, y: phase >= 3 ? 0 : 20 }}
                        transition={{ duration: 0.6, delay: 1 + i * 0.08 }}
                        className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-5 hover:bg-white/[0.04] hover:border-white/[0.1] transition-all duration-300 cursor-pointer group"
                      >
                        <div className={`w-11 h-11 rounded-xl ${colors.bg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                          <feature.icon className={`w-5 h-5 ${colors.text}`} strokeWidth={1.5} />
                        </div>
                        <p className="text-[15px] text-white/90 font-medium mb-1">{feature.title}</p>
                        <p className="text-[13px] text-white/40">{feature.desc}</p>
                      </motion.div>
                    );
                  })}
                </motion.div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Phase 3: Mobile simplified app preview */}
        <motion.div
          className="absolute inset-0 z-10 flex md:hidden flex-col items-center justify-center px-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 3 ? 1 : 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* App mockup card */}
          <div className="w-full max-w-sm bg-[#0f0f12] rounded-2xl border border-white/[0.08] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="h-12 flex items-center px-4 border-b border-white/[0.06] bg-[#0a0a0c]">
              <div className="w-7 h-7 bg-gradient-to-r from-violet-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Zap className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="ml-2 font-medium text-sm text-white/90">Lexy</span>
            </div>
            
            {/* Content */}
            <div className="p-4 space-y-3">
              {/* Feature highlights */}
              {['AI Workspace', 'Case Management', 'Time Tracking', 'Invoicing'].map((feature, i) => (
                <motion.div
                  key={feature}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: phase >= 3 ? 1 : 0, x: phase >= 3 ? 0 : -10 }}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  className="flex items-center gap-3 py-2"
                >
                  <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-violet-500" />
                  </div>
                  <span className="text-white/60 text-sm">{feature}</span>
                </motion.div>
              ))}
            </div>
          </div>
          
          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: phase >= 3 ? 1 : 0 }}
            transition={{ delay: 0.8 }}
            className="mt-6 text-white/30 text-sm text-center"
          >
            All-in-one legal workspace
          </motion.p>
        </motion.div>
      </div>
    );
  };

  // ============================================================================
  // SLIDE 7: PROBLEM - Dramatic, Dark
  // ============================================================================
  const ProblemSlide = () => (
    <div className="relative w-full h-full overflow-hidden flex items-center justify-center bg-[#08080a]">
      {/* Background effects - same as slide 2 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ x: [0, 30, 0], y: [0, -20, 0], opacity: [0.35, 0.5, 0.35] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -top-20 -right-20 w-[700px] h-[700px] rounded-full bg-purple-500/30 blur-[150px]"
        />
        <motion.div
          animate={{ x: [0, -20, 0], y: [0, 30, 0], opacity: [0.25, 0.4, 0.25] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          className="absolute -bottom-20 -left-20 w-[600px] h-[600px] rounded-full bg-indigo-500/25 blur-[130px]"
        />
      </div>
      
      {/* Content */}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-8 text-center">
        {/* Label */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="text-red-400/80 text-sm uppercase tracking-[0.3em] mb-8"
        >
          The Problem
        </motion.p>
        
        {/* Main statement - massive */}
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="text-5xl md:text-6xl lg:text-7xl font-light text-white leading-[1.1] mb-16 tracking-[-0.03em]"
        >
          Lawyers spend
          <br />
          <span className="text-red-400">50% of their day</span>
          <br />
          on admin work.
        </motion.h2>
        
        {/* Pain points - minimal */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 text-slate-400 text-lg"
        >
          <span>Searching emails</span>
          <span className="hidden md:block text-slate-700">•</span>
          <span>Copying data</span>
          <span className="hidden md:block text-slate-700">•</span>
          <span>Switching tools</span>
          <span className="hidden md:block text-slate-700">•</span>
          <span>Logging time</span>
        </motion.div>
        
        {/* Bottom stat */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-20 pt-12 border-t border-slate-800/50"
        >
          <p className="text-slate-500 text-xl">
            Zero systems have <span className="text-white font-medium">end-to-end context</span> on the matter.
          </p>
        </motion.div>
      </div>
    </div>
  );

  // ============================================================================
  // SLIDE 4: WHY NOW - Three Pillars
  // ============================================================================
  const WhyNowSlide = () => {
    const pillars = [
      {
        number: '01',
        title: 'AI Capability',
        description: 'LLMs now reason at near-lawyer level on routine legal tasks.',
      },
      {
        number: '02',
        title: 'Market Pressure',
        description: 'Clients demand speed, transparency, and fixed fees.',
      },
      {
        number: '03',
        title: 'Legacy Tools',
        description: 'Practice management software is 20 years behind.',
      },
    ];

    return (
      <div className="relative w-full h-full overflow-hidden flex items-center justify-center bg-[#08080a]">
        {/* Background effects - same as slide 2 */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ x: [0, 30, 0], y: [0, -20, 0], opacity: [0.35, 0.5, 0.35] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -top-20 -right-20 w-[700px] h-[700px] rounded-full bg-purple-500/30 blur-[150px]"
          />
          <motion.div
            animate={{ x: [0, -20, 0], y: [0, 30, 0], opacity: [0.25, 0.4, 0.25] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            className="absolute -bottom-20 -left-20 w-[600px] h-[600px] rounded-full bg-indigo-500/25 blur-[130px]"
          />
        </div>
        
        {/* Content */}
        <div className="relative z-10 w-full max-w-6xl mx-auto px-8">
          {/* Label */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-violet-400/80 text-sm uppercase tracking-[0.3em] mb-6 text-center"
          >
            Why Now
          </motion.p>
          
          {/* Main headline */}
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-5xl md:text-6xl font-light text-white text-center mb-20 leading-tight tracking-[-0.03em]"
          >
            The perfect storm.
          </motion.h2>
          
          {/* Three pillars */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4">
            {pillars.map((pillar, index) => (
              <motion.div
                key={pillar.number}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 + index * 0.15 }}
                className="text-center md:text-left"
              >
                <span className="text-violet-500 text-sm font-mono tracking-wider">{pillar.number}</span>
                <h3 className="text-2xl font-semibold text-white mt-3 mb-4">{pillar.title}</h3>
                <p className="text-slate-400 text-lg leading-relaxed">{pillar.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // SLIDE 5: VISION - Inbox to Invoice
  // ============================================================================
  const VisionSlide = () => {
    const steps = ['Inbox', 'Case', 'Documents', 'Time', 'Invoice'];

    return (
      <div className="relative w-full h-full overflow-hidden flex items-center justify-center bg-[#08080a]">
        {/* Background effects - same as slide 2 */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ x: [0, 30, 0], y: [0, -20, 0], opacity: [0.35, 0.5, 0.35] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -top-20 -right-20 w-[700px] h-[700px] rounded-full bg-purple-500/30 blur-[150px]"
          />
          <motion.div
            animate={{ x: [0, -20, 0], y: [0, 30, 0], opacity: [0.25, 0.4, 0.25] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            className="absolute -bottom-20 -left-20 w-[600px] h-[600px] rounded-full bg-indigo-500/25 blur-[130px]"
          />
        </div>
        
        {/* Content */}
        <div className="relative z-10 w-full max-w-5xl mx-auto px-8 text-center">
          {/* Label */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-emerald-400/80 text-sm uppercase tracking-[0.3em] mb-6"
          >
            Our Vision
          </motion.p>
          
          {/* Main headline */}
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-5xl md:text-6xl lg:text-7xl font-light text-white mb-6 leading-tight tracking-[-0.03em]"
          >
            One system.
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-2xl md:text-3xl text-slate-400 mb-20"
          >
            From inbox to invoice.
          </motion.p>
          
          {/* Flow diagram - Apple style */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="flex items-center justify-center gap-2 md:gap-4"
          >
            {steps.map((step, index) => (
              <React.Fragment key={step}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                  className="flex flex-col items-center"
                >
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700/50 flex items-center justify-center">
                    <span className="text-white font-medium text-sm md:text-base">{step}</span>
                  </div>
                </motion.div>
                {index < steps.length - 1 && (
                  <ArrowRight className="w-5 h-5 text-slate-600 flex-shrink-0" />
                )}
              </React.Fragment>
            ))}
          </motion.div>
          
          {/* AI Layer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-12"
          >
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-violet-500/10 border border-violet-500/20">
              <Sparkles className="w-4 h-4 text-violet-400" />
              <span className="text-violet-300 text-sm font-medium">AI-powered at every step</span>
            </div>
          </motion.div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // SLIDE 6: PRODUCT - Light, Premium UI
  // ============================================================================
  const ProductSlide = () => {
    const features = [
      'AI-first legal operating system',
      'Connects email, cases, documents, time, billing',
      'Works in Outlook + Word where lawyers live',
    ];

    return (
      <div className="relative w-full h-full overflow-hidden flex items-center justify-center bg-[#FAFAFA]">
        {/* Subtle gradient */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-violet-100/30 to-transparent" />
        
        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-8 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left - Text */}
            <div>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="text-violet-600 text-sm uppercase tracking-[0.2em] mb-6 font-medium"
              >
                The Product
              </motion.p>
              
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="text-5xl md:text-6xl font-light text-slate-900 mb-12 leading-tight tracking-[-0.03em]"
              >
                What Lexy is.
              </motion.h2>
              
              <div className="space-y-6">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                    className="flex items-start gap-4"
                  >
                    <div className="w-6 h-6 rounded-full bg-violet-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-xl text-slate-700">{feature}</p>
                  </motion.div>
                ))}
              </div>
            </div>
            
            {/* Right - Product mockup */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden bg-white shadow-2xl shadow-slate-200/80 border border-slate-200/80">
                {/* Browser chrome */}
                <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border-b border-slate-100">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-slate-300" />
                    <div className="w-3 h-3 rounded-full bg-slate-300" />
                    <div className="w-3 h-3 rounded-full bg-slate-300" />
                  </div>
                  <div className="flex-1 mx-8">
                    <div className="h-7 bg-slate-100 rounded-lg flex items-center justify-center">
                      <span className="text-xs text-slate-400">app.lexy.no</span>
                    </div>
                  </div>
                </div>
                
                {/* App content */}
                <div className="p-6 bg-white">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center text-white font-semibold text-sm">H</div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900">Good morning</p>
                        <p className="text-xs text-slate-500">Thursday, Dec 11</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <p className="text-2xl font-bold text-slate-900">3</p>
                      <p className="text-xs text-slate-500">Active cases</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <p className="text-2xl font-bold text-slate-900">12</p>
                      <p className="text-xs text-slate-500">Emails</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <p className="text-2xl font-bold text-slate-900">2</p>
                      <p className="text-xs text-slate-500">Tasks</p>
                    </div>
                  </div>
                  
                  {/* AI Card */}
                  <div className="rounded-xl bg-gradient-to-br from-slate-900 to-slate-800 p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 rounded-lg bg-violet-500 flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-white font-medium">Lexy AI</span>
                      <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    </div>
                    <div className="h-10 bg-slate-700/50 rounded-lg flex items-center px-4">
                      <span className="text-slate-400 text-sm">Ask about cases, documents...</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Decorative blur */}
              <div className="absolute -inset-8 bg-gradient-to-br from-violet-200/40 via-transparent to-purple-200/40 rounded-3xl blur-3xl -z-10" />
            </motion.div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // SLIDE 7: WORKFLOW - Light, Clean Process
  // ============================================================================
  const WorkflowSlide = () => {
    const steps = [
      {
        number: '01',
        title: 'Inbox',
        description: 'AI triage, emails → matters, attachments → documents',
      },
      {
        number: '02',
        title: 'Case',
        description: 'AI workspace for questions, analysis, and strategy',
      },
      {
        number: '03',
        title: 'Documents',
        description: 'Drafting, redlining, track changes in Word',
      },
      {
        number: '04',
        title: 'Time & Billing',
        description: 'Auto-capture, AI descriptions, invoice generation',
      },
    ];

    return (
      <div className="relative w-full h-full overflow-hidden flex items-center justify-center bg-[#FAFAFA]">
        {/* Subtle gradient */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-violet-100/50 rounded-full blur-[200px]" />
        
        {/* Content */}
        <div className="relative z-10 w-full max-w-6xl mx-auto px-8">
          {/* Label */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-violet-600 text-sm uppercase tracking-[0.2em] mb-6 text-center font-medium"
          >
            End-to-End Workflow
          </motion.p>
          
          {/* Main headline */}
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-5xl md:text-6xl font-light text-slate-900 text-center mb-20 leading-tight tracking-[-0.03em]"
          >
            How Lexy flows.
          </motion.h2>
          
          {/* Steps - horizontal timeline */}
          <div className="relative">
            {/* Connection line */}
            <div className="absolute top-8 left-[12%] right-[12%] h-0.5 bg-gradient-to-r from-violet-200 via-violet-400 to-violet-200" />
            
            {/* Steps */}
            <div className="grid grid-cols-4 gap-4">
              {steps.map((step, index) => (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                  className="flex flex-col items-center text-center"
                >
                  {/* Number circle */}
                  <div className="w-16 h-16 rounded-full bg-violet-600 flex items-center justify-center mb-6 shadow-lg shadow-violet-600/30 relative z-10">
                    <span className="text-white font-bold text-lg">{index + 1}</span>
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{step.title}</h3>
                  
                  {/* Description */}
                  <p className="text-slate-500 text-sm leading-relaxed">{step.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
          
          {/* Bottom tagline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center mt-16"
          >
            <p className="text-xl text-slate-600">
              Every action becomes <span className="text-slate-900 font-medium">structured context</span> for the next.
            </p>
          </motion.div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // SLIDE 8: PRODUCT WALKTHROUGH - Cinematic Demo Video
  // ============================================================================
  const WalkthroughSlide = () => {
    const videoRef = React.useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);

    const handlePlay = () => {
      if (videoRef.current) {
        videoRef.current.play();
        setIsPlaying(true);
      }
    };

    return (
      <div className="relative w-full h-full overflow-hidden flex items-center justify-center bg-[#08080a]">
        {/* Background effects - same as slide 2 */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ x: [0, 30, 0], y: [0, -20, 0], opacity: [0.35, 0.5, 0.35] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -top-20 -right-20 w-[700px] h-[700px] rounded-full bg-purple-500/30 blur-[150px]"
          />
          <motion.div
            animate={{ x: [0, -20, 0], y: [0, 30, 0], opacity: [0.25, 0.4, 0.25] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            className="absolute -bottom-20 -left-20 w-[600px] h-[600px] rounded-full bg-indigo-500/25 blur-[130px]"
          />
        </div>
        
        {/* Content */}
        <div className="relative z-10 w-full max-w-5xl mx-auto px-8">
          {/* Label */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center text-violet-400/80 text-sm uppercase tracking-[0.3em] mb-4"
          >
            Product Demo
          </motion.p>
          
          {/* Title */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-5xl md:text-6xl font-light text-white text-center mb-10 tracking-[-0.03em]"
          >
            See Lexy in action.
          </motion.h2>
          
          {/* Video container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-2xl overflow-hidden bg-slate-900 shadow-2xl shadow-black/80"
          >
            <video
              ref={videoRef}
              className="w-full"
              style={{ aspectRatio: '16/9' }}
              src="https://customer-assets.emergentagent.com/job_slideshow-fix-1/artifacts/bcfn6qqi_Lexy%20System%20Walkthrough.mp4"
              controls={isPlaying}
              playsInline
              preload="auto"
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
            />
            
            {/* Play button overlay */}
            {!isPlaying && (
              <div 
                className="absolute inset-0 flex items-center justify-center cursor-pointer bg-black/40 hover:bg-black/30 transition-all duration-500"
                onClick={handlePlay}
              >
                <motion.div 
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.3 }}
                  className="w-24 h-24 bg-white/95 rounded-full flex items-center justify-center shadow-2xl"
                >
                  <Play className="w-10 h-10 text-black ml-1" fill="black" />
                </motion.div>
              </div>
            )}
          </motion.div>
          
          {/* Bottom tagline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-center text-slate-400 text-xl mt-10"
          >
            From first email to invoice in one connected AI workflow.
          </motion.p>
        </div>
      </div>
    );
  };

  // ============================================================================
  // SLIDE 9: ARCHITECTURE - AI Under the Hood (Lightweight)
  // ============================================================================
  const ArchitectureSlide = () => {
    const pillars = [
      {
        title: 'RAG on matters',
        description: 'AI answers and drafts based on matter emails, docs, notes.',
        icon: (
          <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
        ),
      },
      {
        title: 'Action graph',
        description: 'Every action (open, edit, send, save) feeds into timelines and suggestions.',
        icon: (
          <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z" />
          </svg>
        ),
      },
      {
        title: 'Safety & compliance',
        description: 'EU-hosted data, no training on client data, redaction, audit logs.',
        icon: (
          <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
          </svg>
        ),
      },
    ];

    return (
      <div className="relative w-full h-full overflow-hidden flex items-center justify-center bg-[#08080a]">
        {/* Background effects - same as slide 2 */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ x: [0, 30, 0], y: [0, -20, 0], opacity: [0.35, 0.5, 0.35] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -top-20 -right-20 w-[700px] h-[700px] rounded-full bg-purple-500/30 blur-[150px]"
          />
          <motion.div
            animate={{ x: [0, -20, 0], y: [0, 30, 0], opacity: [0.25, 0.4, 0.25] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            className="absolute -bottom-20 -left-20 w-[600px] h-[600px] rounded-full bg-indigo-500/25 blur-[130px]"
          />
        </div>
        
        {/* Content */}
        <div className="relative z-10 w-full max-w-6xl mx-auto px-8">
          {/* Label */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-cyan-400/80 text-sm uppercase tracking-[0.3em] mb-6 text-center"
          >
            Under the Hood
          </motion.p>
          
          {/* Main headline */}
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-5xl md:text-6xl font-light text-white text-center mb-6 leading-tight tracking-[-0.03em]"
          >
            AI is the workflow,
            <br />
            <span className="text-slate-500">not a button.</span>
          </motion.h2>
          
          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-slate-500 text-xl text-center mb-16 max-w-2xl mx-auto"
          >
            Intelligence woven into every step of the legal workflow.
          </motion.p>
          
          {/* Three pillars */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pillars.map((pillar, index) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                className="text-center"
              >
                {/* Icon */}
                <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-slate-800/50 border border-slate-700/50 flex items-center justify-center text-cyan-400">
                  {pillar.icon}
                </div>
                
                {/* Title */}
                <h3 className="text-xl font-semibold text-white mb-3">{pillar.title}</h3>
                
                {/* Description */}
                <p className="text-slate-400 leading-relaxed">{pillar.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // SLIDE 10: CUSTOMER - Who We're Winning With First
  // ============================================================================
  const CustomerSlide = () => {
    const targetPoints = [
      'Solo and small firms (1–10 lawyers)',
      'High email/document load, little internal IT',
      'Need "done-for-them" setup, not a toolkit',
    ];

    return (
      <div className="relative w-full h-full overflow-hidden flex items-center justify-center bg-[#08080a]">
        {/* Background effects - same as slide 2 */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ x: [0, 30, 0], y: [0, -20, 0], opacity: [0.35, 0.5, 0.35] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -top-20 -right-20 w-[700px] h-[700px] rounded-full bg-purple-500/30 blur-[150px]"
          />
          <motion.div
            animate={{ x: [0, -20, 0], y: [0, 30, 0], opacity: [0.25, 0.4, 0.25] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            className="absolute -bottom-20 -left-20 w-[600px] h-[600px] rounded-full bg-indigo-500/25 blur-[130px]"
          />
        </div>
        
        {/* Content */}
        <div className="relative z-10 w-full max-w-6xl mx-auto px-8">
          {/* Label */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-amber-400/80 text-sm uppercase tracking-[0.3em] mb-6 text-center"
          >
            Go-to-Market
          </motion.p>
          
          {/* Main headline */}
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-5xl md:text-6xl font-light text-white text-center mb-16 leading-tight tracking-[-0.03em]"
          >
            Starting with the
            <br />
            <span className="text-amber-400">modern small practice.</span>
          </motion.h2>
          
          {/* Two column layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left - Target description */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h3 className="text-2xl font-semibold text-white mb-6">V1 Target</h3>
              <div className="space-y-4">
                {targetPoints.map((point, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <div className="w-2 h-2 rounded-full bg-amber-400 mt-2 flex-shrink-0" />
                    <p className="text-lg text-slate-300">{point}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            {/* Right - Persona card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="space-y-6"
            >
              {/* Persona */}
              <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                    M
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-white">Maria, 38</h4>
                    <p className="text-slate-400">Employment law practice, 3 lawyers</p>
                  </div>
                </div>
                <p className="text-slate-300 text-lg italic leading-relaxed">
                  "Spends evenings billing and chasing documents instead of being with her family."
                </p>
              </div>
              
              {/* Early traction / quote */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="p-5 rounded-xl bg-amber-500/10 border border-amber-500/20"
              >
                <p className="text-amber-300 text-sm uppercase tracking-wider mb-2">Early pilot feedback</p>
                <p className="text-white text-lg">
                  "This is exactly what I've been looking for – a system that actually understands my workflow."
                </p>
                <p className="text-slate-500 text-sm mt-2">— Solo practitioner, Oslo</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // SLIDE 11: COMPANY - The Team Behind Lexy
  // ============================================================================
  const CompanySlide = () => {
    const [phase, setPhase] = useState(0);

    useEffect(() => {
      const timers = [
        setTimeout(() => setPhase(1), 600),   // Show "The Team" label
        setTimeout(() => setPhase(2), 2200),  // Transition to split content
      ];
      return () => timers.forEach(clearTimeout);
    }, []);

    return (
      <div className="relative w-full h-full overflow-hidden bg-[#08080a]">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ 
              x: [0, 20, 0], 
              y: [0, -15, 0], 
              opacity: [0.25, 0.35, 0.25]
            }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -top-32 -right-32 w-[800px] h-[800px] rounded-full bg-violet-600/25 blur-[180px]"
          />
          <motion.div
            animate={{ x: [0, -15, 0], y: [0, 20, 0], opacity: [0.2, 0.3, 0.2] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            className="absolute -bottom-32 -left-32 w-[700px] h-[700px] rounded-full bg-indigo-600/20 blur-[160px]"
          />
        </div>

        {/* Phase 1: Opening label - same as slide 3 */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 1 && phase < 2 ? 1 : 0 }}
          transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <span className="text-[15px] font-[400] text-white/40 tracking-[0.4em] uppercase">
            The Team
          </span>
        </motion.div>

        {/* Phase 2: Split screen content - stacks on mobile */}
        <motion.div
          className="absolute inset-0 flex flex-col md:flex-row z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 2 ? 1 : 0 }}
          transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Left side - Logo and Company */}
          <motion.div 
            className="w-full md:w-1/2 flex flex-col items-center justify-center py-8 md:py-0 md:border-r border-white/[0.04]"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: phase >= 2 ? 1 : 0, x: phase >= 2 ? 0 : -30 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Logo "a." - responsive size */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ 
                opacity: phase >= 2 ? 1 : 0, 
                scale: phase >= 2 ? 1 : 0.9,
                y: phase >= 2 ? [0, -8, 0] : 0
              }}
              transition={{ 
                opacity: { duration: 0.8, delay: 0.3 },
                scale: { duration: 0.8, delay: 0.3 },
                y: { duration: 4, repeat: Infinity, ease: "easeInOut" }
              }}
              className="flex justify-center mb-2 md:mb-4"
            >
              <svg width="120" height="120" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="md:w-[220px] md:h-[220px]">
                <text x="50%" y="80" textAnchor="middle" fontFamily="system-ui, -apple-system, sans-serif" fontSize="95" fontWeight="200" fill="white">a.</text>
              </svg>
            </motion.div>
            
            {/* Company name */}
            <motion.h1
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 15 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-2xl md:text-4xl font-extralight text-white/80 tracking-[0.15em] lowercase text-center -mt-2 md:-mt-4"
              style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
            >
              autonomous
            </motion.h1>

            {/* Subtext */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: phase >= 2 ? 1 : 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="mt-6 md:mt-12 text-center"
            >
              <p className="text-white/30 text-sm md:text-base font-light">Autonomous AS</p>
              <p className="text-white/20 text-xs md:text-sm font-light mt-1 md:mt-2">Norwegian, AI-native product company</p>
            </motion.div>
          </motion.div>

          {/* Right side - Team */}
          <motion.div 
            className="w-full md:w-1/2 flex flex-col items-center justify-center px-8 md:px-16 pb-16 md:pb-0"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: phase >= 2 ? 1 : 0, x: phase >= 2 ? 0 : 30 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Team cards */}
            <div className="space-y-6 md:space-y-10 w-full max-w-md">
              {/* Martin */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 20 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex items-center gap-4 md:gap-6"
              >
                <div className="relative flex-shrink-0">
                  <div className="absolute -inset-1 bg-gradient-to-br from-violet-500/20 to-purple-500/10 rounded-full blur-sm" />
                  <img 
                    src="https://customer-assets.emergentagent.com/job_magical-lovelace/artifacts/l3bdrazw_603a4205317945628ebfbb5a_Martin.jpg"
                    alt="Martin Kviteberg"
                    className="relative w-14 h-14 md:w-20 md:h-20 rounded-full object-cover border border-white/[0.08]"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-white font-medium text-base md:text-xl tracking-tight">Martin Kviteberg</h4>
                  <p className="text-violet-400/70 text-xs md:text-sm mt-0.5 md:mt-1">CEO & Founder</p>
                  <p className="text-white/30 text-xs md:text-sm mt-1 md:mt-2 leading-relaxed hidden md:block">Product, development & GTM</p>
                </div>
              </motion.div>

              {/* Erik */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 20 }}
                transition={{ duration: 0.6, delay: 0.75 }}
                className="flex items-center gap-4 md:gap-6"
              >
                <div className="relative flex-shrink-0">
                  <div className="absolute -inset-1 bg-gradient-to-br from-violet-500/20 to-purple-500/10 rounded-full blur-sm" />
                  <img 
                    src="https://customer-assets.emergentagent.com/job_magical-lovelace/artifacts/a79g9eik_images%20%285%29.jpeg"
                    alt="Erik Hoffmann-Dahl"
                    className="relative w-14 h-14 md:w-20 md:h-20 rounded-full object-cover border border-white/[0.08]"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-white font-medium text-base md:text-xl tracking-tight">Erik Hoffmann-Dahl</h4>
                  <p className="text-violet-400/70 text-xs md:text-sm mt-0.5 md:mt-1">Partner & Lawyer</p>
                  <p className="text-white/30 text-xs md:text-sm mt-1 md:mt-2 leading-relaxed hidden md:block">Legal expertise & workflows</p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    );
  };

  // ============================================================================
  // SLIDE 9: AI NATIVE - Combined with video and AI employees
  // ============================================================================
  const AINativeSlide = () => {
    const [phase, setPhase] = useState(0);
    const videoRef = React.useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);

    useEffect(() => {
      const timers = [
        setTimeout(() => setPhase(1), 600),
        setTimeout(() => setPhase(2), 2200),
      ];
      return () => timers.forEach(clearTimeout);
    }, []);

    const handlePlay = () => {
      if (videoRef.current) {
        videoRef.current.play();
        setIsPlaying(true);
      }
    };

    const aiTeam = [
      { 
        name: 'Alexander', 
        role: 'AI CEO & Spokesperson', 
        image: 'https://customer-assets.emergentagent.com/job_magical-lovelace/artifacts/y1f52f3a_jci8ZJbpWK77OAoLZFMjQ_d2f6980e-b584-40a8-bdba-bfaa8666c371.jpeg',
      },
      { 
        name: 'Emma', 
        role: 'AI Head of Marketing', 
        image: 'https://customer-assets.emergentagent.com/job_magical-lovelace/artifacts/22fg55ok_fcd0a588cfce4fb785dfa3bbde7395b0%20%281%29.webp',
      },
      { 
        name: 'Daniel', 
        role: 'AI CTO', 
        image: 'https://customer-assets.emergentagent.com/job_magical-lovelace/artifacts/nbcmvbul_OWK1hNmnEtQjHFytWbbaq%20%281%29.jpg',
      },
    ];

    return (
      <div className="relative w-full h-full overflow-hidden bg-[#08080a]">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ 
              x: [0, 20, 0], 
              y: [0, -15, 0], 
              opacity: [0.25, 0.35, 0.25]
            }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -top-32 -right-32 w-[800px] h-[800px] rounded-full bg-violet-600/25 blur-[180px]"
          />
          <motion.div
            animate={{ x: [0, -15, 0], y: [0, 20, 0], opacity: [0.2, 0.3, 0.2] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            className="absolute -bottom-32 -left-32 w-[700px] h-[700px] rounded-full bg-indigo-600/20 blur-[160px]"
          />
        </div>

        {/* Phase 1: Opening label */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 1 && phase < 2 ? 1 : 0 }}
          transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <span className="text-[15px] font-[400] text-white/40 tracking-[0.4em] uppercase">
            AI Native
          </span>
        </motion.div>

        {/* Phase 2: 50/50 Split layout - stacks on mobile */}
        <motion.div
          className="absolute inset-0 flex flex-col md:flex-row z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 2 ? 1 : 0 }}
          transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Left side - Large Video with text below */}
          <motion.div 
            className="w-full md:w-1/2 flex flex-col items-center justify-center p-4 md:p-8 md:border-r border-white/[0.04]"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: phase >= 2 ? 1 : 0, x: phase >= 2 ? 0 : -30 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Large Vertical Video */}
            <div className="relative h-[50vh] md:h-[80%] max-h-[350px] md:max-h-[580px]" style={{ aspectRatio: '9/16' }}>
              {/* Glow effect */}
              <motion.div
                animate={{ opacity: [0.1, 0.25, 0.1] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -inset-4 md:-inset-6 bg-gradient-to-b from-violet-500/15 via-purple-500/10 to-violet-500/15 rounded-2xl md:rounded-3xl blur-2xl md:blur-3xl"
              />
              <div className="relative w-full h-full rounded-xl md:rounded-2xl overflow-hidden shadow-2xl shadow-black/50 border border-white/[0.06] bg-black">
                <video
                  ref={videoRef}
                  className="w-full h-full object-contain"
                  controls={isPlaying}
                  playsInline
                  preload="auto"
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onEnded={() => setIsPlaying(false)}
                >
                  <source src="https://customer-assets.emergentagent.com/job_magical-lovelace/artifacts/ml2duwud_Lexy%20CTO%205.mp4" type="video/mp4" />
                </video>
                
                {!isPlaying && (
                  <div 
                    className="absolute inset-0 flex items-center justify-center cursor-pointer bg-gradient-to-t from-black/50 via-transparent to-transparent"
                    onClick={handlePlay}
                  >
                    <motion.div 
                      whileHover={{ scale: 1.1 }}
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                      className="w-14 h-14 md:w-20 md:h-20 bg-white rounded-full flex items-center justify-center shadow-2xl"
                    >
                      <Play className="w-6 h-6 md:w-8 md:h-8 text-gray-900 ml-0.5 md:ml-1" fill="currentColor" />
                    </motion.div>
                  </div>
                )}
              </div>
            </div>

            {/* Text below video */}
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 10 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-white/40 text-xs md:text-sm font-light mt-4 md:mt-6 text-center max-w-[200px] md:max-w-xs"
            >
              Watch our CTO Daniel explain the benefits of agent-based development
            </motion.p>
          </motion.div>

          {/* Right side - AI Employees - Super minimalist */}
          <motion.div 
            className="w-full md:w-1/2 flex flex-col justify-center px-6 md:px-20 pb-16 md:pb-0"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: phase >= 2 ? 1 : 0, x: phase >= 2 ? 0 : 30 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <h2 className="text-2xl md:text-[3rem] font-extralight text-white tracking-[-0.02em] leading-[1.1] mb-6 md:mb-12">
              Meet our AI Team.
            </h2>

            <div className="space-y-4 md:space-y-8">
              {aiTeam.map((member, i) => (
                <motion.div
                  key={member.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 20 }}
                  transition={{ duration: 0.5, delay: 0.5 + i * 0.12 }}
                  className="flex items-center gap-4 md:gap-6"
                >
                  <div className="relative flex-shrink-0">
                    <div className="absolute -inset-1 bg-gradient-to-br from-violet-500/20 to-purple-500/10 rounded-full blur-md" />
                    <img 
                      src={member.image}
                      alt={member.name}
                      className="relative w-12 h-12 md:w-20 md:h-20 rounded-full object-cover border border-white/[0.06]"
                    />
                    <div className="absolute -bottom-0.5 -right-0.5 bg-gradient-to-r from-violet-500 to-purple-500 text-white text-[7px] md:text-[9px] font-semibold px-1.5 md:px-2 py-0.5 rounded-full shadow-lg">
                      AI
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white font-medium text-base md:text-xl mb-0.5 md:mb-1">{member.name}</h4>
                    <p className="text-violet-400/60 text-xs md:text-sm">{member.role}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    );
  };

  // ============================================================================
  // SLIDE 10: VELOCITY - AI-Native Build Speed with Video
  // ============================================================================
  const VelocitySlide = () => {
    const [phase, setPhase] = useState(0);
    const videoRef = React.useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);

    useEffect(() => {
      const timers = [
        setTimeout(() => setPhase(1), 600),
        setTimeout(() => setPhase(2), 2200),
      ];
      return () => timers.forEach(clearTimeout);
    }, []);

    const handlePlay = () => {
      if (videoRef.current) {
        videoRef.current.play();
        setIsPlaying(true);
      }
    };

    return (
      <div className="relative w-full h-full overflow-hidden bg-[#08080a]">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ 
              x: [0, 20, 0], 
              y: [0, -15, 0], 
              opacity: [0.25, 0.35, 0.25]
            }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -top-32 -right-32 w-[800px] h-[800px] rounded-full bg-violet-600/25 blur-[180px]"
          />
          <motion.div
            animate={{ x: [0, -15, 0], y: [0, 20, 0], opacity: [0.2, 0.3, 0.2] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            className="absolute -bottom-32 -left-32 w-[700px] h-[700px] rounded-full bg-indigo-600/20 blur-[160px]"
          />
        </div>

        {/* Phase 1: Opening label */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 1 && phase < 2 ? 1 : 0 }}
          transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <span className="text-[15px] font-[400] text-white/40 tracking-[0.4em] uppercase">
            Build Velocity
          </span>
        </motion.div>

        {/* Phase 2: Split layout content */}
        <motion.div
          className="absolute inset-0 flex z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 2 ? 1 : 0 }}
          transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Left side - Video 16:9 */}
          <motion.div 
            className="w-[55%] flex items-center justify-center p-8 border-r border-white/[0.04]"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: phase >= 2 ? 1 : 0, x: phase >= 2 ? 0 : -30 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="relative w-full max-w-2xl" style={{ aspectRatio: '16/9' }}>
              {/* Glow effect */}
              <motion.div
                animate={{ opacity: [0.3, 0.5, 0.3] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -inset-4 bg-gradient-to-r from-violet-500/10 via-purple-500/15 to-violet-500/10 rounded-3xl blur-2xl"
              />
              <div className="relative w-full h-full rounded-2xl overflow-hidden shadow-2xl shadow-black/50 border border-white/[0.08]">
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  controls={isPlaying}
                  playsInline
                  preload="metadata"
                  poster="https://customer-assets.emergentagent.com/job_magical-lovelace/artifacts/nbcmvbul_OWK1hNmnEtQjHFytWbbaq%20%281%29.jpg"
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onEnded={() => setIsPlaying(false)}
                >
                  <source src="https://customer-assets.emergentagent.com/job_magical-lovelace/artifacts/ml2duwud_Lexy%20CTO%205.mp4" type="video/mp4" />
                </video>
                
                {!isPlaying && (
                  <div 
                    className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer bg-gradient-to-t from-black/60 via-black/30 to-black/20 hover:from-black/50 hover:via-black/20 hover:to-black/10 transition-all duration-500"
                    onClick={handlePlay}
                  >
                    <motion.div 
                      whileHover={{ scale: 1.1 }}
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                      className="w-20 h-20 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-2xl mb-4"
                    >
                      <Play className="w-8 h-8 text-gray-900 ml-1" fill="currentColor" />
                    </motion.div>
                    <p className="text-white font-medium text-lg">Daniel</p>
                    <p className="text-white/50 text-sm">AI CTO on agent-based development</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Right side - Content */}
          <motion.div 
            className="w-[45%] flex flex-col justify-center px-10"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: phase >= 2 ? 1 : 0, x: phase >= 2 ? 0 : 30 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <h2 className="text-[2.8rem] font-[200] text-white tracking-[-0.03em] leading-[1.15] mb-10">
              Multi-agent
              <br />
              <span className="text-white/30">development.</span>
            </h2>

            <div className="space-y-3">
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: phase >= 2 ? 1 : 0, x: phase >= 2 ? 0 : 20 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
                    <Zap className="w-4 h-4 text-violet-400" />
                  </div>
                  <div>
                    <span className="text-white font-medium text-sm">4 weeks to prototype</span>
                    <p className="text-white/30 text-xs">Zero to working product with AI</p>
                  </div>
                </div>
              </motion.div>
              
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: phase >= 2 ? 1 : 0, x: phase >= 2 ? 0 : 20 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
                    <Users className="w-4 h-4 text-violet-400" />
                  </div>
                  <div>
                    <span className="text-white font-medium text-sm">2 humans + AI team</span>
                    <p className="text-white/30 text-xs">Output of 10-15 person team</p>
                  </div>
                </div>
              </motion.div>
              
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: phase >= 2 ? 1 : 0, x: phase >= 2 ? 0 : 20 }}
                transition={{ duration: 0.5, delay: 0.7 }}
                className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-violet-400" />
                  </div>
                  <div>
                    <span className="text-white font-medium text-sm">Capital efficient</span>
                    <p className="text-white/30 text-xs">Ship fast with minimal burn</p>
                  </div>
                </div>
              </motion.div>
            </div>

            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: phase >= 2 ? 1 : 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="text-white/20 text-sm italic mt-8 border-l-2 border-violet-500/30 pl-4"
            >
              "We can ship like a large team, with the burn of a very small one."
            </motion.p>
          </motion.div>
        </motion.div>
      </div>
    );
  };

  // ============================================================================
  // SLIDE 11: BUSINESS MODEL - Pricing
  // ============================================================================
  const BusinessModelSlide = () => {
    const [phase, setPhase] = useState(0);

    useEffect(() => {
      const timers = [
        setTimeout(() => setPhase(1), 600),
        setTimeout(() => setPhase(2), 2200),
      ];
      return () => timers.forEach(clearTimeout);
    }, []);

    return (
      <div className="relative w-full h-full overflow-hidden bg-[#08080a]">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ 
              x: [0, 20, 0], 
              y: [0, -15, 0], 
              opacity: [0.2, 0.3, 0.2]
            }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -top-32 -right-32 w-[800px] h-[800px] rounded-full bg-violet-600/20 blur-[180px]"
          />
          <motion.div
            animate={{ x: [0, -15, 0], y: [0, 20, 0], opacity: [0.15, 0.25, 0.15] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            className="absolute -bottom-32 -left-32 w-[700px] h-[700px] rounded-full bg-indigo-600/15 blur-[160px]"
          />
        </div>

        {/* Phase 1: Opening label */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 1 && phase < 2 ? 1 : 0 }}
          transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <span className="text-[15px] font-[400] text-white/40 tracking-[0.4em] uppercase">
            Business Model
          </span>
        </motion.div>

        {/* Phase 2: Pricing tiers */}
        <motion.div
          className="absolute inset-0 flex flex-col items-center justify-center z-10 px-4 md:px-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 2 ? 1 : 0 }}
          transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Main headline */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 20 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-2xl sm:text-4xl md:text-[3.5rem] font-extralight text-white tracking-[-0.03em] text-center mb-2 md:mb-4"
          >
            Simple pricing.
          </motion.h2>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 15 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-white/30 text-sm md:text-lg font-light text-center max-w-lg mb-8 md:mb-12"
          >
            Start free, scale as you grow.
          </motion.p>

          {/* Pricing cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 20 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col md:flex-row items-center md:items-end gap-4 md:gap-6"
          >
            {/* Pro */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 20 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="w-full max-w-[200px] md:w-48 p-4 md:p-5 rounded-xl md:rounded-2xl bg-white/[0.02] border border-white/[0.06]"
            >
              <h3 className="text-white font-medium text-base md:text-lg mb-2">Pro</h3>
              <div className="flex items-baseline mb-3">
                <span className="text-2xl md:text-3xl font-light text-white">990</span>
                <span className="text-white/30 text-xs md:text-sm ml-1">NOK/mo</span>
              </div>
              <ul className="space-y-1.5 text-xs md:text-sm text-white/40">
                <li>1 user</li>
                <li>Unlimited matters</li>
                <li>AI Assistant</li>
              </ul>
            </motion.div>

            {/* Team - Featured */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 20 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="w-full max-w-[220px] md:w-56 p-4 md:p-6 rounded-xl md:rounded-2xl bg-gradient-to-b from-violet-500/10 to-purple-500/5 border-2 border-violet-500/30 scale-100 md:scale-105"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-white font-medium text-base md:text-lg">Team</h3>
                <span className="text-[9px] md:text-[10px] text-violet-400 bg-violet-500/20 px-2 py-0.5 rounded-full font-medium">POPULAR</span>
              </div>
              <div className="flex items-baseline mb-3">
                <span className="text-2xl md:text-3xl font-light text-white">2 490</span>
                <span className="text-white/30 text-xs md:text-sm ml-1">NOK/mo</span>
              </div>
              <ul className="space-y-1.5 text-xs md:text-sm text-white/50">
                <li>Up to 5 users</li>
                <li>Everything in Pro</li>
                <li>Team collaboration</li>
                <li>Priority support</li>
              </ul>
            </motion.div>

            {/* Enterprise */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 20 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="w-full max-w-[200px] md:w-48 p-4 md:p-5 rounded-xl md:rounded-2xl bg-white/[0.02] border border-white/[0.06]"
            >
              <h3 className="text-white font-medium text-base md:text-lg mb-2">Enterprise</h3>
              <div className="flex items-baseline mb-3">
                <span className="text-2xl md:text-3xl font-light text-white">Custom</span>
              </div>
              <ul className="space-y-1.5 text-xs md:text-sm text-white/40">
                <li>Unlimited users</li>
                <li>Everything in Team</li>
                <li>Custom integrations</li>
                <li>Dedicated support</li>
              </ul>
            </motion.div>
          </motion.div>

          {/* Bottom tagline */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: phase >= 2 ? 1 : 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="text-white/20 text-xs md:text-sm mt-8 md:mt-12 tracking-wide text-center"
          >
            14-day free trial • No credit card required
          </motion.p>
        </motion.div>
      </div>
    );
  };

  // ============================================================================
  // SLIDE: MARKET VALIDATION - Elegant Minimalist
  // ============================================================================
  const MarketValidationSlide = () => {
    const [phase, setPhase] = useState(0);

    useEffect(() => {
      const timers = [
        setTimeout(() => setPhase(1), 500),
        setTimeout(() => setPhase(2), 2200),
      ];
      return () => timers.forEach(clearTimeout);
    }, []);

    return (
      <div className="relative w-full h-full overflow-hidden bg-[#08080a]">
        {/* Subtle background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ opacity: [0.06, 0.1, 0.06] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-white/5 blur-[200px]"
          />
        </div>

        {/* Phase 1: Label */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 1 && phase < 2 ? 1 : 0 }}
          transition={{ duration: 1.2 }}
        >
          <span className="text-[15px] font-[400] text-white/40 tracking-[0.4em] uppercase">
            Market Validation
          </span>
        </motion.div>

        {/* Phase 2: Content */}
        <motion.div
          className="absolute inset-0 flex flex-col items-center justify-center z-10 px-6 md:px-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 2 ? 1 : 0 }}
          transition={{ duration: 1 }}
        >
          {/* Main statement */}
          <motion.h2
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 25 }}
            transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-[200] text-white tracking-[-0.02em] text-center mb-16 md:mb-24 max-w-4xl leading-[1.1]"
          >
            Small firms already pay.
            <br />
            <span className="text-white/40">Just not for AI.</span>
          </motion.h2>

          {/* Two key stats */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: phase >= 2 ? 1 : 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="flex flex-col md:flex-row items-center justify-center gap-16 md:gap-32"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 20 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-center"
            >
              <p className="text-5xl md:text-7xl font-[200] text-white tracking-tight mb-3">
                €4k<span className="text-white/30">+</span>
              </p>
              <p className="text-white/40 text-sm md:text-base font-light tracking-wide">
                spent annually per lawyer
              </p>
            </motion.div>

            <div className="hidden md:block w-px h-20 bg-white/[0.08]" />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 20 }}
              transition={{ duration: 0.8, delay: 0.65 }}
              className="text-center"
            >
              <p className="text-5xl md:text-7xl font-[200] text-white tracking-tight mb-3">
                5<span className="text-white/30">+</span>
              </p>
              <p className="text-white/40 text-sm md:text-base font-light tracking-wide">
                disconnected tools
              </p>
            </motion.div>
          </motion.div>

          {/* Subtle insight at bottom */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: phase >= 2 ? 1 : 0 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="absolute bottom-12 md:bottom-16 text-white/25 text-sm md:text-base font-light text-center"
          >
            Consolidation is inevitable. AI makes it urgent.
          </motion.p>
        </motion.div>
      </div>
    );
  };

  // ============================================================================
  // SLIDE: MARKET SIZE - Elegant Minimalist
  // ============================================================================
  const MarketSizeSlide = () => {
    const [phase, setPhase] = useState(0);

    useEffect(() => {
      const timers = [
        setTimeout(() => setPhase(1), 500),
        setTimeout(() => setPhase(2), 2200),
      ];
      return () => timers.forEach(clearTimeout);
    }, []);

    return (
      <div className="relative w-full h-full overflow-hidden bg-[#08080a]">
        {/* Subtle background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ opacity: [0.05, 0.08, 0.05] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full bg-violet-500/10 blur-[200px]"
          />
        </div>

        {/* Phase 1: Label */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 1 && phase < 2 ? 1 : 0 }}
          transition={{ duration: 1.2 }}
        >
          <span className="text-[15px] font-[400] text-white/40 tracking-[0.4em] uppercase">
            Market Size
          </span>
        </motion.div>

        {/* Phase 2: Content */}
        <motion.div
          className="absolute inset-0 flex flex-col items-center justify-center z-10 px-6 md:px-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 2 ? 1 : 0 }}
          transition={{ duration: 1 }}
        >
          {/* TAM - The big number */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 30 }}
            transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="text-center mb-16 md:mb-20"
          >
            <p className="text-white/30 text-sm md:text-base uppercase tracking-[0.2em] mb-4">
              Total Addressable Market
            </p>
            <p className="text-6xl sm:text-7xl md:text-9xl font-[200] text-white tracking-tight">
              $18B
            </p>
            <p className="text-white/40 text-base md:text-lg font-light mt-4">
              Global solo & small law firms
            </p>
          </motion.div>

          {/* SAM and SOM in a row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 20 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex flex-col md:flex-row items-center justify-center gap-12 md:gap-24"
          >
            <div className="text-center">
              <p className="text-white/25 text-xs uppercase tracking-[0.15em] mb-2">SAM · Europe</p>
              <p className="text-3xl md:text-5xl font-[200] text-white/80 tracking-tight">$2.5B</p>
            </div>

            <div className="hidden md:block w-px h-12 bg-white/[0.06]" />

            <div className="text-center">
              <p className="text-white/25 text-xs uppercase tracking-[0.15em] mb-2">SOM · Year 1</p>
              <p className="text-3xl md:text-5xl font-[200] text-violet-400 tracking-tight">$300M</p>
            </div>
          </motion.div>

          {/* Bottom insight */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: phase >= 2 ? 1 : 0 }}
            transition={{ duration: 1, delay: 0.8 }}
            className="absolute bottom-12 md:bottom-16 text-white/25 text-sm md:text-base font-light text-center"
          >
            Bottom-up SaaS. Fast decisions. High intent.
          </motion.p>
        </motion.div>
      </div>
    );
  };

  // ============================================================================
  // SLIDE: COMPETITIVE LANDSCAPE - Elegant Minimalist
  // ============================================================================
  const CompetitiveLandscapeSlide = () => {
    const [phase, setPhase] = useState(0);

    useEffect(() => {
      const timers = [
        setTimeout(() => setPhase(1), 500),
        setTimeout(() => setPhase(2), 2200),
      ];
      return () => timers.forEach(clearTimeout);
    }, []);

    return (
      <div className="relative w-full h-full overflow-hidden bg-[#08080a]">
        {/* Subtle background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ opacity: [0.04, 0.07, 0.04] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full bg-white/5 blur-[200px]"
          />
        </div>

        {/* Phase 1: Label */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 1 && phase < 2 ? 1 : 0 }}
          transition={{ duration: 1.2 }}
        >
          <span className="text-[15px] font-[400] text-white/40 tracking-[0.4em] uppercase">
            Competitive Landscape
          </span>
        </motion.div>

        {/* Phase 2: Content */}
        <motion.div
          className="absolute inset-0 flex flex-col items-center justify-center z-10 px-6 md:px-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 2 ? 1 : 0 }}
          transition={{ duration: 1 }}
        >
          {/* Main headline */}
          <motion.h2
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 25 }}
            transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="text-3xl sm:text-4xl md:text-6xl font-[200] text-white tracking-[-0.02em] text-center mb-6 md:mb-10 max-w-4xl leading-[1.1]"
          >
            Everyone owns the case
            <br />
            <span className="text-white/40">or the AI. Not both.</span>
          </motion.h2>

          {/* Simple comparison - Desktop */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 20 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="hidden md:flex items-stretch gap-4 w-full max-w-5xl"
          >
            {/* Legacy Tools */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 15 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex-1 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6"
            >
              <p className="text-white/30 text-xs uppercase tracking-[0.15em] mb-4">Legacy Case Tools</p>
              <p className="text-white/60 text-lg font-light mb-3">Owns the case.</p>
              <p className="text-white/30 text-sm font-light mb-6">Built pre-AI. AI bolted on as features.</p>
              <p className="text-white/20 text-xs">Clio · PracticePanther · MyCase</p>
            </motion.div>

            {/* AI Solutions */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 15 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex-1 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6"
            >
              <p className="text-white/30 text-xs uppercase tracking-[0.15em] mb-4">AI Point Solutions</p>
              <p className="text-white/60 text-lg font-light mb-3">Owns the AI.</p>
              <p className="text-white/30 text-sm font-light mb-6">Powerful but narrow. No case context.</p>
              <p className="text-white/20 text-xs">Harvey · CoCounsel · Spellbook</p>
            </motion.div>

            {/* Lexy */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 15 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="flex-1 rounded-2xl border border-violet-500/30 bg-violet-500/[0.04] p-6 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-40 h-40 bg-violet-500/10 blur-[80px] rounded-full" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                    <Zap className="w-3 h-3 text-white" />
                  </div>
                  <p className="text-violet-400 text-xs uppercase tracking-[0.15em]">Lexy</p>
                </div>
                <p className="text-white text-lg font-light mb-3">Owns both.</p>
                <p className="text-white/50 text-sm font-light mb-6">AI-native from day one. Full case context.</p>
                <p className="text-violet-400/60 text-xs">Built from scratch for AI</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Mobile version */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 20 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="md:hidden w-full max-w-sm space-y-3"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: phase >= 2 ? 1 : 0 }}
              transition={{ delay: 0.5 }}
              className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4"
            >
              <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Legacy Tools</p>
              <p className="text-white/50 text-sm">Owns the case. AI bolted on.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: phase >= 2 ? 1 : 0 }}
              transition={{ delay: 0.6 }}
              className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4"
            >
              <p className="text-white/40 text-xs uppercase tracking-wider mb-2">AI Solutions</p>
              <p className="text-white/50 text-sm">Owns the AI. No case context.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: phase >= 2 ? 1 : 0 }}
              transition={{ delay: 0.7 }}
              className="rounded-xl border border-violet-500/30 bg-violet-500/[0.04] p-4"
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 rounded-md bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                  <Zap className="w-2.5 h-2.5 text-white" />
                </div>
                <p className="text-violet-400 text-sm font-medium">Lexy</p>
              </div>
              <p className="text-white/70 text-sm">Owns both. Built for AI.</p>
            </motion.div>
          </motion.div>

          {/* Footer */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: phase >= 2 ? 1 : 0 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="absolute bottom-12 md:bottom-16 text-white/25 text-sm md:text-base font-light text-center"
          >
            Legacy vendors can add AI. They can't remove their architecture.
          </motion.p>
        </motion.div>
      </div>
    );
  };

  // ============================================================================
  // SLIDE: COMPETITIVE ADVANTAGE - Why Lexy Wins
  // ============================================================================
  const CompetitiveAdvantageSlide = () => {
    const [phase, setPhase] = useState(0);

    useEffect(() => {
      const timers = [
        setTimeout(() => setPhase(1), 500),
        setTimeout(() => setPhase(2), 2200),
      ];
      return () => timers.forEach(clearTimeout);
    }, []);

    const advantages = [
      {
        title: 'AI-native architecture',
        description: 'Built around AI from day one. No legacy constraints.',
      },
      {
        title: 'Agent-powered execution',
        description: 'AI across development, testing, and go-to-market.',
      },
      {
        title: 'Compounding intelligence',
        description: 'Case data creates defensible workflow lock-in.',
      },
    ];

    return (
      <div className="relative w-full h-full overflow-hidden bg-[#08080a]">
        {/* Subtle background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ opacity: [0.05, 0.09, 0.05] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-violet-500/10 blur-[180px]"
          />
        </div>

        {/* Phase 1: Label */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 1 && phase < 2 ? 1 : 0 }}
          transition={{ duration: 1.2 }}
        >
          <span className="text-[15px] font-[400] text-white/40 tracking-[0.4em] uppercase">
            Competitive Advantage
          </span>
        </motion.div>

        {/* Phase 2: Content */}
        <motion.div
          className="absolute inset-0 flex flex-col items-center justify-center z-10 px-6 md:px-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 2 ? 1 : 0 }}
          transition={{ duration: 1 }}
        >
          {/* Main headline */}
          <motion.h2
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 25 }}
            transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="text-3xl sm:text-4xl md:text-6xl font-[200] text-white tracking-[-0.02em] text-center mb-4 md:mb-6 max-w-3xl leading-[1.1]"
          >
            Why Lexy wins.
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 15 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-white/40 text-base md:text-xl font-light text-center mb-12 md:mb-16"
          >
            Different economics. Not just different features.
          </motion.p>

          {/* Three pillars */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 20 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16 w-full max-w-4xl"
          >
            {advantages.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 20 }}
                transition={{ duration: 0.6, delay: 0.5 + i * 0.15 }}
                className="text-center max-w-xs"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-white/[0.04] border border-white/[0.06] flex items-center justify-center mx-auto mb-5">
                  <span className="text-2xl md:text-3xl font-[200] text-white/60">{i + 1}</span>
                </div>
                <p className="text-white text-lg md:text-xl font-light mb-2 tracking-tight">
                  {item.title}
                </p>
                <p className="text-white/40 text-sm md:text-base font-light leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </motion.div>

          {/* Footer */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: phase >= 2 ? 1 : 0 }}
            transition={{ duration: 1, delay: 0.95 }}
            className="absolute bottom-12 md:bottom-16 text-white/25 text-sm md:text-base font-light text-center"
          >
            AI-first creates fundamentally different unit economics.
          </motion.p>
        </motion.div>
      </div>
    );
  };

  // Render current slide
  const renderSlide = (slideIndex: number) => {
    const slide = slides[slideIndex];
    switch (slide.type) {
      case 'cover': return <CoverSlide onStartPresentation={() => paginate(1)} />;
      case 'founder-video': return <FounderVideoSlide />;
      case 'features-bento': return <FeaturesBentoSlide />;
      case 'lexy-walkthrough': return <LexyWalkthroughSlide />;
      case 'market-need': return <MarketNeedSlide />;
      case 'how-we-solve': return <HowWeSolveSlide />;
      case 'introducing-lexy': return <IntroducingLexySlide />;
      case 'problem': return <ProblemSlide />;
      case 'why-now': return <WhyNowSlide />;
      case 'market-validation': return <MarketValidationSlide />;
      case 'market-size': return <MarketSizeSlide />;
      case 'competitive-landscape': return <CompetitiveLandscapeSlide />;
      case 'competitive-advantage': return <CompetitiveAdvantageSlide />;
      case 'vision': return <VisionSlide />;
      case 'product': return <ProductSlide />;
      case 'workflow': return <WorkflowSlide />;
      case 'walkthrough': return <WalkthroughSlide />;
      case 'architecture': return <ArchitectureSlide />;
      case 'customer': return <CustomerSlide />;
      case 'company': return <CompanySlide />;
      case 'ai-native': return <AINativeSlide />;
      case 'business-model': return <BusinessModelSlide />;
      default: return null;
    }
  };

  // Check if current slide is light themed (no light slides in current deck)
  const isLightSlide = false;

  return (
    <div className="fixed inset-0 overflow-hidden bg-[#08080a]">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Lexy - The AI Platform for Modern Law Firms | Investor Presentation</title>
        <meta name="description" content="Lexy is an AI-native legal platform that streamlines case management, time tracking, and invoicing. One system from inbox to invoice, powered by AI employees that work around the clock." />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Lexy - The AI Platform for Modern Law Firms" />
        <meta property="og:description" content="AI-native legal platform. One system from inbox to invoice. Case management, time tracking, invoicing - all powered by AI." />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_minimalist-design-9/artifacts/lexy-og-image.png" />
        <meta property="og:url" content="https://lexy.ai/investor-presentation" />
        <meta property="og:site_name" content="Lexy" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Lexy - The AI Platform for Modern Law Firms" />
        <meta name="twitter:description" content="AI-native legal platform. One system from inbox to invoice. Case management, time tracking, invoicing - all powered by AI." />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_minimalist-design-9/artifacts/lexy-og-image.png" />
        
        {/* Additional SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="keywords" content="legal tech, AI, law firm software, case management, time tracking, invoicing, legal AI, Lexy" />
        <link rel="canonical" href="https://lexy.ai/investor-presentation" />
      </Helmet>

      {/* Slides container */}
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={currentSlide}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.6, ease: [0.32, 0.72, 0, 1] }}
          className="absolute inset-0"
        >
          {renderSlide(currentSlide)}
        </motion.div>
      </AnimatePresence>

      {/* Navigation - Ultra minimal, responsive */}
      {currentSlide === 0 ? (
        /* Cover slide - no navigation bar, uses Start Presentation button */
        null
      ) : (
        /* All other slides - normal navigation */
        <div className="absolute bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 md:gap-4">
          {/* Previous - Ghost */}
          <button
            onClick={() => paginate(-1)}
            disabled={currentSlide === 0}
            className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
              currentSlide === 0 ? 'opacity-0 pointer-events-none' : 'opacity-40 hover:opacity-70'
            } ${isLightSlide ? 'text-slate-500' : 'text-white'}`}
          >
            <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
          </button>

          {/* Dots - Minimal */}
          <div className="flex items-center gap-1 md:gap-1.5">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setDirection(index > currentSlide ? 1 : -1);
                  setCurrentSlide(index);
                }}
                className={`rounded-full transition-all duration-500 ease-out ${
                  index === currentSlide
                    ? `w-4 md:w-6 h-1 md:h-1.5 ${isLightSlide ? 'bg-slate-800' : 'bg-white'}`
                    : `w-1 md:w-1.5 h-1 md:h-1.5 ${isLightSlide ? 'bg-slate-300 hover:bg-slate-400' : 'bg-white/20 hover:bg-white/40'}`
                }`}
              />
            ))}

          </div>

          {/* Next - Simple circle */}
          <button
            onClick={() => paginate(1)}
            disabled={currentSlide === slides.length - 1}
            className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
              currentSlide === slides.length - 1
                ? 'opacity-0 pointer-events-none'
                : 'opacity-60 hover:opacity-100'
            } ${isLightSlide 
              ? 'bg-slate-200 text-slate-600 hover:bg-slate-300' 
              : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        </div>
      )}

      {/* Top controls */}
      <div className="absolute top-4 right-4 md:top-6 md:right-6 z-50">
        {/* Fullscreen */}
        <button
          onClick={toggleFullscreen}
          className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-all hover:scale-110 ${
            isLightSlide ? 'bg-slate-200 text-slate-600' : 'bg-white/10 text-white/70 hover:text-white'
          }`}
        >
          <Maximize className="w-3.5 h-3.5 md:w-4 md:h-4" />
        </button>
      </div>
    </div>
  );
};

export default InvestorPresentation;
